import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MetaTags from 'react-meta-tags';
import SweetAlert from 'react-bootstrap-sweetalert';
import { AvForm } from 'availity-reactstrap-validation';
import { Table, Card, CardBody, Col, Container, Row, Alert, FormGroup, Label, Input, InputGroup, InputGroupText, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { AvField, AvCheckboxGroup, AvCheckbox, AvRadioGroup, AvRadio } from 'availity-reactstrap-validation';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import { motion, AnimatePresence } from 'framer-motion';
import Dropzone from 'react-dropzone';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

import classnames from 'classnames';
import Select, { components } from 'react-select';
import makeAnimated from 'react-select/animated';

import { pricesApi } from '../../services/PriceService';

import Breadcrumbs from '../../components/Breadcrumb';
import PageLoader from '../../components/Loaders/newPageLoader';
import { showToast } from '../../components/Toast';
import { columnHeaders, headerTranslations, headerPriceTranslations, priceTranslation } from '../../helpers/currency_helper';
import useLoadingProgress from '../../hooks/useLoadingProgress';
import { MainContent } from '../../components/Table/MainContent';
import { BtnModal } from '../../components/Table/BtnModal';
import { BadgePlus } from 'lucide-react';
// import banner from '../../assets/images/banner.jpg';
import best_seller_img from '../../assets/images/best_seller.png';
import koru_logo from '../../assets/images/koru_logo.png';
import geologicaRegularFont from '../../assets/fonts/Geologica-Regular.ttf';
import geologicaMediumFont from '../../assets/fonts/Geologica-Medium.ttf';

import manropeLightFont from '../../assets/fonts/Manrope-Light.ttf';
import manropeMediumFont from '../../assets/fonts/Manrope-Medium.ttf';
import manropeExtraBoldFont from '../../assets/fonts/Manrope-ExtraBold.ttf';
import notoSansSCRegularFont from '../../assets/fonts/NotoSansSC-Regular.ttf';
import notoSansJPRegularFont from '../../assets/fonts/NotoSansJP-Regular.ttf';
/**
 * Generates a header array for a price list table based on product category, column labels, and configuration options.
 *
 * The function dynamically includes or excludes certain columns such as composition, needle type, description, and price,
 * depending on the properties of the provided product data and the specified options.
 *
 * @param {Object|Object[]} catProducts - The product category data or an array of product objects.
 * @param {string[]} columns - The default column labels for the header.
 * @param {Object} values - Configuration values, including language selection (`expLng`).
 * @param {boolean} includeWAmountChecked - Whether to include additional columns for amount.
 * @param {boolean} includeWAmountAddChecked - Whether to include extra columns for additional amount.
 * @returns {Array} The generated header array, with possible objects for cells requiring colspan and styling.
 */
// function createHeader(catProducts, columns, values, includeWAmountChecked, includeWAmountAddChecked) {
//   const { expLng } = values;
//   const first = Array.isArray(catProducts) ? catProducts[0] : catProducts;
//   const t = (headerTranslations && headerTranslations[expLng]) || columns;

//   if (!first) return [];

//   const lang = (first.languages && first.languages[expLng]) || {};
//   const hasComposition = !!lang.composition;
//   const hasNeedle = !!lang.needle_type;
//   const hasDescription = !!lang.description;

//   const priceCell = (label) => {
//     if (includeWAmountAddChecked) {
//       return { content: label, styles: { valign: 'middle', halign: 'center' }, colSpan: 3 };
//     }
//     if (includeWAmountChecked) {
//       return { content: label, styles: { valign: 'middle', halign: 'center' }, colSpan: 2 };
//     }
//     return label;
//   };

//   return t.reduce((acc, label, i) => {
//     if (i === 0 || i === 1) acc.push(label);
//     else if (i === 2 && hasComposition) acc.push(label);
//     else if ([3, 4, 5, 6, 7, 8].includes(i) && hasNeedle) acc.push(label);
//     else if (i === 9) acc.push(priceCell(label));
//     else if (i === 10 && hasDescription) acc.push(label);
//     return acc;
//   }, []);
// }
function createHeader(catProducts, columns, values, includeWAmountChecked, includeWAmountAddChecked) {
  const { expLng } = values;

  const products = Array.isArray(catProducts) ? catProducts : [catProducts];
  const first = products[0];

  const t =
    (headerTranslations && headerTranslations[expLng]) ||
    columns.map(col => col.text || col);

  if (!first) return [];

  const lang = (first.languages && first.languages[expLng]) || {};

  const hasComposition = !!lang.composition;
  const hasNeedle = !!lang.needle_type;
  const hasDescription = !!lang.description;

  const productWithWAmountAdd = products.find(
    product =>
      product &&
      product.w_amount_add !== undefined &&
      product.w_amount_add !== null &&
      product.w_amount_add !== ''
  );

  const hasWAmountAdd = !!productWithWAmountAdd;

  const formatAmount = (amount) => {
    if (!amount) return '';

    const numbers =
      String(amount).match(/\d+(?:[.,]\d+)?/g) || [];

    // Example: "from 1 to 29" -> "(1~29)"
    if (numbers.length >= 2) {
      return `(${numbers[0]}~${numbers[1]})`;
    }

    // Example: "from 100" -> "(100~)"
    if (numbers.length === 1) {
      return `(${numbers[0]}~)`;
    }

    return '';
  };

  const priceHeader = (label, amount) => {
    const formattedAmount = formatAmount(amount);

    return formattedAmount
      ? `${label}\n${formattedAmount}`
      : label;
  };

  return t.reduce((acc, label, i) => {
    if (i === 0 || i === 1) {
      acc.push(label);
    } else if (i === 2 && hasComposition) {
      acc.push(label);
    } else if ([3, 4, 5, 6, 7, 8].includes(i) && hasNeedle) {
      acc.push(label);
    } else if (i === 9) {
      const retailOnly =
        !includeWAmountChecked &&
        !includeWAmountAddChecked;

      // Retail price
      acc.push(
        retailOnly
          ? label
          : priceHeader(label, first.r_amount)
      );

      // Wholesale price
      if (includeWAmountChecked) {
        acc.push(
          priceHeader(label, first.w_amount)
        );
      }

      // Additional wholesale price
      if (
        includeWAmountAddChecked &&
        hasWAmountAdd
      ) {
        acc.push(
          priceHeader(
            label,
            productWithWAmountAdd.w_amount_add
          )
        );
      }
    } else if (i === 10 && hasDescription) {
      acc.push(label);
    }

    return acc;
  }, []);
}
/**
 * Generates an array of row data for a product, formatted for display in a price list.
 *
 * This function extracts and formats product information, including localized fields,
 * pricing details, and additional attributes based on provided selection values and flags.
 * It supports conditional inclusion of wholesale prices and additional wholesale prices,
 * and applies formatting such as currency symbols and column spans for table rendering.
 *
 * @param {Object} product - The product object containing details and pricing information.
 * @param {string|number} catId - The category ID associated with the product.
 * @param {Object} values - Selection values and filters, including language and region info.
 * @param {boolean} includeWAmountChecked - Flag indicating whether to include wholesale price.
 * @param {boolean} includeWAmountAddChecked - Flag indicating whether to include additional wholesale price.
 * @param {boolean} hasNonEmptyWAmountAdd - Flag indicating if there is a non-empty additional wholesale amount.
 * @returns {Array} An array of formatted row data for the product, suitable for table display.
 */
function createArrayData(product, catId, values, includeWAmountChecked, includeWAmountAddChecked, hasNonEmptyWAmountAdd) {
  let rowData = [];
  let { expDef, expLng } = values;

  let { best_seller, r_amount, w_amount, w_amount_add, pic, prices, regions } = product;
  let {
    title = product.languages['eng'].title,
    composition = product.languages['eng'].composition,
    description = product.languages['eng'].description,
    needle_type = product.languages['eng'].needle_type,
    needle_gage = product.languages['eng'].needle_gage,
    needle_mm = product.languages['eng'].needle_mm,
    thread_type = product.languages['eng'].thread_type,
    thread_gage = product.languages['eng'].thread_gage,
    thread_mm = product.languages['eng'].thread_mm
  } = product.languages[expLng] ?? {};
  const keysToCheck = ['expCont', 'expCntr', 'expSt', 'expCt'];
  const selFilter = keysToCheck.filter((key) => values[key] !== '').map((key) => ({ key, value: values[key] }));
  const selectedPrices =
    expDef !== '' ? prices[expDef] : { price_r: regions[selFilter[0].value].price_r, price_w: regions[selFilter[0].value].price_w, price_w_add: regions[selFilter[0].value].price_w_add };
  let r_price, w_price, w_price_add;
  r_price = selectedPrices.price_r === null ? null : getCurrencySymbol(selFilter[0]?.value, selectedPrices.price_r) + selectedPrices.price_r;
  w_price = selectedPrices.price_w === null ? null : getCurrencySymbol(selFilter[0]?.value, selectedPrices.price_w) + selectedPrices.price_w;
  const rawWPriceAdd = selectedPrices.price_w_add; 
  w_price_add = rawWPriceAdd === null || rawWPriceAdd === undefined || String(rawWPriceAdd).trim() === '' ? null : getCurrencySymbol(selFilter[0]?.value, rawWPriceAdd) + rawWPriceAdd;

  rowData.push(
    { content: title, styles: { minCellHeight: 10 } },
    '',
    composition,
    needle_type,
    needle_gage,
    needle_mm,
    thread_type,
    thread_gage,
    thread_mm,
    // {r_price: r_price !== null ? r_price : '' },
    r_price !== null ? r_price : '',
    w_price !== null && includeWAmountChecked === true ? { content: w_price, colSpan: (w_price_add === null || w_price_add === '') &&
            includeWAmountAddChecked === true &&
            hasNonEmptyWAmountAdd === true ? 2 : 1
        }
      : null,
    w_amount_add !== '' && w_price_add !== null && includeWAmountAddChecked === true ? w_price_add : null,
    description,
    best_seller,
    catId,
    r_amount,
    w_amount,
    w_amount_add,
    pic
  );
  const newData = rowData.filter((item) => item !== undefined && item !== null);
  return newData;
}
/**
 * Returns the currency symbol based on the provided region and price.
 * If the price is a string containing "You can", returns an empty string.
 * Otherwise, returns "£" for "gb" (Great Britain), "¥" for "cn" (China), and "$" for all other regions.
 *
 * @param {string} region - The region code (e.g., "gb", "cn").
 * @param {string|number} price - The price value, which may be a string or number.
 * @returns {string} The corresponding currency symbol or an empty string.
 */
function getCurrencySymbol(region, price) {
  if (typeof price === 'string' && price.includes('You can')) {
    return '';
  }

  switch (region) {
    case 'gb':
      return '£';
    case 'cn':
      return '¥';
    default:
      return '$';
  }
}
async function loadPdfFont(
  doc,
  url,
  fileName,
  fontFamily
) {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `Failed to load font ${url}: ${response.status}`
    );
  }

  const arrayBuffer =
    await response.arrayBuffer();

  const bytes =
    new Uint8Array(arrayBuffer);

  const signature = Array
    .from(bytes.slice(0, 4))
    .map(byte =>
      byte
        .toString(16)
        .padStart(2, '0')
    )
    .join(' ');

  let binary = '';

  for (
    let i = 0;
    i < bytes.length;
    i++
  ) {
    binary +=
      String.fromCharCode(bytes[i]);
  }

  const base64 =
    btoa(binary);

  doc.addFileToVFS(
    fileName,
    base64
  );

  doc.addFont(
    fileName,
    fontFamily,
    'normal',
    'Identity-H'
  );
}
const TABLE_MARGIN_X = 5;
const TABLE_WIDTH_MM = 210 - TABLE_MARGIN_X * 2; // 200 mm
const TABLE_TOTAL_WIDTH_PX = 2350;

const TABLE_HEADER_COLOR = [83, 159, 211];
const TABLE_BORDER_COLOR = [83, 159, 211];
const TABLE_LINE_WIDTH = 0.2;
const TABLE_INNER_LINE_WIDTH = 0.1;
const TABLE_CORNER_OVERLAP = 0.1;
const TABLE_RADIUS = 3;
const TABLE_START_Y = 58;

const CATEGORY_CARD_X = TABLE_MARGIN_X;
const CATEGORY_CARD_Y = 6;
const CATEGORY_CARD_W = TABLE_WIDTH_MM;
const CATEGORY_CARD_H = 32;
const CATEGORY_CARD_RADIUS = 4;

const PDF_WHITE = [255, 255, 255];
const CATEGORY_CARD_OPACITY = 0.78;
const CATEGORY_CARD_FILL = [236, 244, 248]; // very light blue
const CATEGORY_CARD_BORDER = [255, 255, 255];

const CATEGORY_PRICE_LIST_SIZE = 17;
const CATEGORY_TITLE_SIZE = 26.7;

const TABLE_HEADER_FONT_SIZE = 9;

const PRODUCT_NAME_FONT_SIZE = 8.9;
const COMPOSITION_FONT_SIZE = 7.7;
const DESCRIPTION_FONT_SIZE = 6.8;
const PRICE_FONT_SIZE = 8.9;
const THREAD_SPEC_FONT_SIZE = 8.9;
const TABLE_LINE_HEIGHT_FACTOR = 1.22;

const FIRST_PAGE_TABLE_START_Y = CATEGORY_CARD_Y + CATEGORY_CARD_H + 4;

const PAGE_BG_GREEN = '#00AD7D';
const PAGE_BG_BLUE = '#0072BC';

const PAGE_GRADIENT_END_Y = 150;
const PAGE_GRADIENT_TOP_LIGHTNESS = 0.25;

const pxToTableWidth = (px) => (px / TABLE_TOTAL_WIDTH_PX) * TABLE_WIDTH_MM;
function drawTableTopRoundedCorners(
  doc,
  topY,
  marginX = 5,
  radius = 3
) {
  const pageWidth = doc.internal.pageSize.getWidth();

  const x = marginX;
  const width = pageWidth - marginX * 2;

  const k = 0.5522847498;
  const overlap = TABLE_CORNER_OVERLAP;

  // 1. Remove original square corners
  // const backgroundColor = getPageBackgroundColorAtY(topY);

// 1. Get actual page background color
// separately for left and right corners

const leftBackgroundColor =
  getPageGradientColorAtPoint(
    doc,
    x + radius / 2,
    topY + radius / 2
  );

const rightBackgroundColor =
  getPageGradientColorAtPoint(
    doc,
    x + width - radius / 2,
    topY + radius / 2
  );

// Remove top-left square corner
doc.setFillColor(...leftBackgroundColor);

doc.rect(
  x - 0.2,
  topY - 0.2,
  radius + 0.4,
  radius + 0.4,
  'F'
);

// Remove top-right square corner
doc.setFillColor(...rightBackgroundColor);

doc.rect(
  x + width - radius - 0.2,
  topY - 0.2,
  radius + 0.4,
  radius + 0.4,
  'F'
);

  // 2. Restore blue header inside rounded corners
  doc.setFillColor(...TABLE_HEADER_COLOR);

  doc.circle(
    x + radius,
    topY + radius,
    radius,
    'F'
  );

  doc.circle(
    x + width - radius,
    topY + radius,
    radius,
    'F'
  );

  // 3. Border settings
  doc.setDrawColor(...TABLE_HEADER_COLOR);
  doc.setLineWidth(TABLE_LINE_WIDTH);

  // 4. Top-left arc
  doc.lines(
    [[
      0,
      -k * radius,
      radius - k * radius,
      -radius,
      radius,
      -radius
    ]],
    x,
    topY + radius,
    [1, 1],
    'S',
    false
  );

  // 5. Top-right arc
  doc.lines(
    [[
      k * radius,
      0,
      radius,
      radius - k * radius,
      radius,
      radius
    ]],
    x + width - radius,
    topY,
    [1, 1],
    'S',
    false
  );

  // 6. Top horizontal line
  doc.line(
    x + radius - overlap,
    topY,
    x + width - radius + overlap,
    topY
  );

  // 7. Join left arc with vertical border
  doc.line(
    x,
    topY + radius - overlap,
    x,
    topY + radius + overlap
  );

  // 8. Join right arc with vertical border
  doc.line(
    x + width,
    topY + radius - overlap,
    x + width,
    topY + radius + overlap
  );
}
function drawTableBottomRoundedCorners(
  doc,
  bottomY,
  marginX = 5,
  radius = 3
) {
  const pageWidth = doc.internal.pageSize.getWidth();

  const x = marginX;
  const width = pageWidth - marginX * 2;

  const k = 0.5522847498;
  const overlap = TABLE_CORNER_OVERLAP;

  // 1. Remove original square corners
// 1. Get actual page background color
// separately for left and right bottom corners

const leftBackgroundColor =
  getPageGradientColorAtPoint(
    doc,
    x + radius / 2,
    bottomY - radius / 2
  );

const rightBackgroundColor =
  getPageGradientColorAtPoint(
    doc,
    x + width - radius / 2,
    bottomY - radius / 2
  );

// Remove bottom-left square corner
doc.setFillColor(...leftBackgroundColor);

doc.rect(
  x - 0.2,
  bottomY - radius - 0.2,
  radius + 0.4,
  radius + 0.4,
  'F'
);

// Remove bottom-right square corner
doc.setFillColor(...rightBackgroundColor);

doc.rect(
  x + width - radius - 0.2,
  bottomY - radius - 0.2,
  radius + 0.4,
  radius + 0.4,
  'F'
);
// Restore white table body inside rounded corners
doc.setFillColor(...PDF_WHITE);

doc.circle(
  x + radius,
  bottomY - radius,
  radius,
  'F'
);

doc.circle(
  x + width - radius,
  bottomY - radius,
  radius,
  'F'
);
  // 2. Border settings
  doc.setDrawColor(...TABLE_BORDER_COLOR);
  doc.setLineWidth(TABLE_LINE_WIDTH);

  // 3. Bottom-left arc
  doc.lines(
    [[
      0,
      k * radius,
      radius - k * radius,
      radius,
      radius,
      radius
    ]],
    x,
    bottomY - radius,
    [1, 1],
    'S',
    false
  );

  // 4. Bottom-right arc
  doc.lines(
    [[
      0,
      k * radius,
      -(radius - k * radius),
      radius,
      -radius,
      radius
    ]],
    x + width,
    bottomY - radius,
    [1, 1],
    'S',
    false
  );

  // 5. Bottom horizontal line
  doc.line(
    x + radius - overlap,
    bottomY,
    x + width - radius + overlap,
    bottomY
  );

  // 6. Join left arc with vertical border
  doc.line(
    x,
    bottomY - radius - overlap,
    x,
    bottomY - radius + overlap
  );

  // 7. Join right arc with vertical border
  doc.line(
    x + width,
    bottomY - radius - overlap,
    x + width,
    bottomY - radius + overlap
  );
}
function createColumnRoles(
  catProducts,
  values,
  includeWAmountChecked,
  includeWAmountAddChecked
) {
  const { expLng } = values;

  const products = Array.isArray(catProducts)
    ? catProducts
    : [catProducts];

  const first = products[0];

  if (!first) return [];

  const lang =
    (first.languages && first.languages[expLng]) || {};

  const hasComposition = !!lang.composition;
  const hasNeedle = !!lang.needle_type;
  const hasDescription = !!lang.description;

  const hasWAmountAdd = products.some(
    product =>
      product &&
      product.w_amount_add !== undefined &&
      product.w_amount_add !== null &&
      product.w_amount_add !== ''
  );

  const roles = [
    'productName',
    'drawing'
  ];

  if (hasComposition) {
    roles.push('composition');
  }

  if (hasNeedle) {
    roles.push(
      'threadSpec', // needle type
      'threadSpec', // needle gauge
      'threadSpec', // needle mm
      'threadSpec', // thread type
      'threadSpec', // thread USP
      'threadSpec'  // thread mm
    );
  }

  // Retail price
  roles.push('price');

  // Wholesale price
  if (includeWAmountChecked) {
    roles.push('price');
  }

  // Additional wholesale price
  if (includeWAmountAddChecked && hasWAmountAdd) {
    roles.push('price');
  }

  if (hasDescription) {
    roles.push('description');
  }

  return roles;
}
function mergeRepeatedPriceRows(
  bodyData,
  columnRoles,
  rowPageMap
) {
  const priceIndexes = columnRoles
    .map((role, index) =>
      role === 'price' ? index : -1
    )
    .filter(index => index !== -1);

  if (!priceIndexes.length) {
    return;
  }

  const priceStartIndex = priceIndexes[0];
  const priceColumnCount = priceIndexes.length;

  const getCellContent = cell => {
    if (
      typeof cell === 'object' &&
      cell !== null &&
      'content' in cell
    ) {
      return cell.content;
    }

    return cell;
  };

  const getColSpan = cell => {
    if (
      typeof cell === 'object' &&
      cell !== null &&
      cell.colSpan
    ) {
      return cell.colSpan;
    }

    return 1;
  };

  // ==================================================
  // IMPORTANT:
  // Rows under Model Name / Drawing rowSpan
  // physically don't contain columns 0 and 1,
  // because newRow.splice(0, 2) was used earlier.
  // ==================================================
  const leadingOffsetByRow = new Map();

  let remainingRowSpanRows = 0;

  for (
    let rowIndex = 0;
    rowIndex < bodyData.length;
    rowIndex++
  ) {
    const row = bodyData[rowIndex];

    if (
      !Array.isArray(row) ||
      row.length === 1
    ) {
      leadingOffsetByRow.set(
        rowIndex,
        0
      );

      remainingRowSpanRows = 0;
      continue;
    }

    // Continuation row:
    // Model Name + Drawing are occupied by rowSpan
    if (remainingRowSpanRows > 0) {
      leadingOffsetByRow.set(
        rowIndex,
        2
      );

      remainingRowSpanRows--;
      continue;
    }

    // First row of product
    leadingOffsetByRow.set(
      rowIndex,
      0
    );

    const firstCell = row[0];
    const secondCell = row[1];

    if (
      typeof firstCell === 'object' &&
      firstCell !== null &&
      firstCell.rowSpan > 1 &&
      typeof secondCell === 'object' &&
      secondCell !== null &&
      secondCell.rowSpan > 1
    ) {
      remainingRowSpanRows =
        firstCell.rowSpan - 1;
    }
  }

  // ==================================================
  // Logical column -> actual array index
  // ==================================================
  const getArrayIndex = (
    row,
    rowIndex,
    logicalColumnIndex
  ) => {
    let currentColumn =
      leadingOffsetByRow.get(rowIndex) || 0;

    for (
      let arrayIndex = 0;
      arrayIndex < row.length;
      arrayIndex++
    ) {
      if (
        currentColumn >=
        columnRoles.length
      ) {
        break;
      }

      const cell = row[arrayIndex];

      const colSpan =
        getColSpan(cell);

      if (
        logicalColumnIndex >= currentColumn &&
        logicalColumnIndex <
          currentColumn + colSpan
      ) {
        return arrayIndex;
      }

      currentColumn += colSpan;
    }

    return -1;
  };

  const getLogicalCell = (
    row,
    rowIndex,
    logicalColumnIndex
  ) => {
    const arrayIndex =
      getArrayIndex(
        row,
        rowIndex,
        logicalColumnIndex
      );

    if (arrayIndex === -1) {
      return null;
    }

    return row[arrayIndex];
  };

  // $9.4 === $9.40
  const normalizePrice = cell => {
    const value = String(
      getCellContent(cell) ?? ''
    ).trim();

    if (!value) {
      return '';
    }

    const numericValue =
      parseFloat(
        value.replace(
          /[^\d.-]/g,
          ''
        )
      );

    if (Number.isNaN(numericValue)) {
      return value;
    }

    const symbol =
      value.match(/^[^\d-]+/)?.[0] || '';

    return (
      symbol +
      numericValue.toFixed(2)
    );
  };

  const getPrices = (
    row,
    rowIndex
  ) =>
    priceIndexes.map(
      logicalIndex =>
        normalizePrice(
          getLogicalCell(
            row,
            rowIndex,
            logicalIndex
          )
        )
    );

  // ==================================================
  // Replace complete logical Price area
  // ==================================================
  const replacePriceArea = (
    row,
    rowIndex,
    replacementCells
  ) => {
    let currentColumn =
      leadingOffsetByRow.get(rowIndex) || 0;

    let startArrayIndex = -1;
    let endArrayIndex = -1;

    const priceEndIndex =
      priceStartIndex +
      priceColumnCount;

    for (
      let arrayIndex = 0;
      arrayIndex < row.length;
      arrayIndex++
    ) {
      if (
        currentColumn >=
        columnRoles.length
      ) {
        break;
      }

      const cell =
        row[arrayIndex];

      const colSpan =
        getColSpan(cell);

      const cellStart =
        currentColumn;

      const cellEnd =
        currentColumn + colSpan;

      if (
        cellStart < priceEndIndex &&
        cellEnd > priceStartIndex
      ) {
        if (
          startArrayIndex === -1
        ) {
          startArrayIndex =
            arrayIndex;
        }

        endArrayIndex =
          arrayIndex;
      }

      currentColumn =
        cellEnd;
    }

    if (
      startArrayIndex === -1
    ) {
      return;
    }

    row.splice(
      startArrayIndex,
      endArrayIndex -
        startArrayIndex +
        1,
      ...replacementCells
    );
  };

  // ==================================================
  // MERGE
  // ==================================================
  let i = 0;

  while (i < bodyData.length) {
    const currentRow =
      bodyData[i];

    // Subcategory row
    if (
      !Array.isArray(currentRow) ||
      currentRow.length === 1
    ) {
      i++;
      continue;
    }

    const currentPrices =
      getPrices(
        currentRow,
        i
      );

    // Nothing to merge
    if (
      currentPrices.every(
        price => price === ''
      )
    ) {
      i++;
      continue;
    }

    const currentPage =
      rowPageMap.get(i);

    let endIndex =
      i + 1;

    // ==================================================
    // VERTICAL:
    // same prices AND same page
    // ==================================================
    while (
      endIndex <
      bodyData.length
    ) {
      const nextRow =
        bodyData[endIndex];

      if (
        !Array.isArray(nextRow) ||
        nextRow.length === 1
      ) {
        break;
      }

      // NEVER merge across page break
      if (
        rowPageMap.get(endIndex) !==
        currentPage
      ) {
        break;
      }

      const nextPrices =
        getPrices(
          nextRow,
          endIndex
        );

      const samePrices =
        currentPrices.every(
          (price, priceIndex) =>
            price ===
            nextPrices[priceIndex]
        );

      if (!samePrices) {
        break;
      }

      endIndex++;
    }

    const rowSpan =
      endIndex - i;

    // ==================================================
    // HORIZONTAL:
    // $2 | $2 -> one cell colSpan 2
    // ==================================================
    const groups = [];

    let priceIndex = 0;

    while (
      priceIndex <
      priceColumnCount
    ) {
      const value =
        currentPrices[priceIndex];

      let size = 1;

      while (
        priceIndex + size <
          priceColumnCount &&
        currentPrices[
          priceIndex + size
        ] === value
      ) {
        size++;
      }

      groups.push({
        start: priceIndex,
        size
      });

      priceIndex += size;
    }

    const replacementCells =
      groups.map(group => {
        const sourceCell =
          getLogicalCell(
            currentRow,
            i,
            priceStartIndex +
              group.start
          );

        return {
          content:
            getCellContent(
              sourceCell
            ),

          colSpan:
            group.size,

          rowSpan,

          styles: {
            halign: 'center',
            valign: 'middle'
          }
        };
      });

    // First row keeps merged Price cell(s)
    replacePriceArea(
      currentRow,
      i,
      replacementCells
    );

    // Following rows are covered by rowSpan
    for (
      let rowIndex = i + 1;
      rowIndex < endIndex;
      rowIndex++
    ) {
      replacePriceArea(
        bodyData[rowIndex],
        rowIndex,
        []
      );
    }

    i = endIndex;
  }
}
function createPdfColumnStyles(
  category,
  catProducts,
  values,
  includeWAmountChecked,
  includeWAmountAddChecked
) {
  const roles = createColumnRoles(
    catProducts,
    values,
    includeWAmountChecked,
    includeWAmountAddChecked
  );

  const categoryName =
    category?.catNameLng?.catName || '';

  const priceIndexes = roles
    .map((role, index) =>
      role === 'price' ? index : -1
    )
    .filter(index => index !== -1);

  const styles = {};

  // ==================================================
  // TEMPLATE C — COG THREADS
  // ==================================================
  if (categoryName === 'COG Threads') {
    const MODEL_WIDTH = 363;
    const DRAWING_WIDTH = 229;
    const NEEDLE_TYPE_WIDTH = 233;
    const NEEDLE_GAUGE_WIDTH = 221;
    const NEEDLE_MM_WIDTH = 157;
    const THREAD_TYPE_WIDTH = 156;
    const THREAD_USP_WIDTH = 157;
    const THREAD_MM_WIDTH = 156;

    // In design:
    // Price 1 = 328
    // Price 2 = 350
    // Total price area = 678
    const PRICE_AREA_WIDTH = 678;

    const fixedWidths = [
      MODEL_WIDTH,
      DRAWING_WIDTH,
      NEEDLE_TYPE_WIDTH,
      NEEDLE_GAUGE_WIDTH,
      NEEDLE_MM_WIDTH,
      THREAD_TYPE_WIDTH,
      THREAD_USP_WIDTH,
      THREAD_MM_WIDTH
    ];

    fixedWidths.forEach((width, index) => {
      styles[index] = {
        cellWidth: pxToTableWidth(width)
      };
    });

    // 1 price column
    if (priceIndexes.length === 1) {
      styles[priceIndexes[0]] = {
        cellWidth: pxToTableWidth(PRICE_AREA_WIDTH)
      };
    }

    // 2 price columns
    if (priceIndexes.length === 2) {
      styles[priceIndexes[0]] = {
        cellWidth: pxToTableWidth(328)
      };

      styles[priceIndexes[1]] = {
        cellWidth: pxToTableWidth(350)
      };
    }

    // 3 price columns
    if (priceIndexes.length >= 3) {
      const priceWidth =
        PRICE_AREA_WIDTH / priceIndexes.length;

      priceIndexes.forEach(index => {
        styles[index] = {
          cellWidth: pxToTableWidth(priceWidth)
        };
      });
    }

    return styles;
  }

  // ==================================================
  // TEMPLATE B — PDO THREADS
  // ==================================================
  if (categoryName === 'PDO Threads') {
    const MODEL_WIDTH = 363;
    const DRAWING_WIDTH = 256;
    const COMPOSITION_WIDTH = 740;
    const DESCRIPTION_WIDTH = 678;

    // On design:
    // Price 1-99 = 157
    // Price 100+ = 156
    // Total price area = 313
    const PRICE_AREA_WIDTH = 313;

    roles.forEach((role, index) => {
      switch (role) {
        case 'productName':
          styles[index] = {
            cellWidth: pxToTableWidth(MODEL_WIDTH)
          };
          break;

        case 'drawing':
          styles[index] = {
            cellWidth: pxToTableWidth(DRAWING_WIDTH)
          };
          break;

        case 'composition':
          styles[index] = {
            cellWidth: pxToTableWidth(COMPOSITION_WIDTH)
          };
          break;

        case 'description':
          styles[index] = {
            cellWidth: pxToTableWidth(DESCRIPTION_WIDTH)
          };
          break;

        default:
          break;
      }
    });

    // Prices
    if (priceIndexes.length === 1) {
      styles[priceIndexes[0]] = {
        cellWidth: pxToTableWidth(PRICE_AREA_WIDTH)
      };
    }

    if (priceIndexes.length === 2) {
      styles[priceIndexes[0]] = {
        cellWidth: pxToTableWidth(157)
      };

      styles[priceIndexes[1]] = {
        cellWidth: pxToTableWidth(156)
      };
    }

    if (priceIndexes.length >= 3) {
      const priceWidth =
        PRICE_AREA_WIDTH / priceIndexes.length;

      priceIndexes.forEach(index => {
        styles[index] = {
          cellWidth: pxToTableWidth(priceWidth)
        };
      });
    }

    return styles;
  }

  // ==================================================
  // TEMPLATE A — STANDARD TABLE
  // ==================================================
  const MODEL_WIDTH = 464;
  const DRAWING_WIDTH = 362;
  const COMPOSITION_WIDTH = 516;
  const DESCRIPTION_WIDTH = 625;

  // Price 1 = 176
  // Price 2 = 177
  const PRICE_AREA_WIDTH = 383;

  roles.forEach((role, index) => {
    switch (role) {
      case 'productName':
        styles[index] = {
          cellWidth: pxToTableWidth(MODEL_WIDTH)
        };
        break;

      case 'drawing':
        styles[index] = {
          cellWidth: pxToTableWidth(DRAWING_WIDTH)
        };
        break;

      case 'composition':
        styles[index] = {
          cellWidth: pxToTableWidth(COMPOSITION_WIDTH)
        };
        break;

      case 'description':
        styles[index] = {
          cellWidth: pxToTableWidth(DESCRIPTION_WIDTH)
        };
        break;

      default:
        break;
    }
  });

  // Prices
  if (priceIndexes.length === 1) {
    styles[priceIndexes[0]] = {
      cellWidth: pxToTableWidth(PRICE_AREA_WIDTH)
    };
  }

  if (priceIndexes.length === 2) {
    styles[priceIndexes[0]] = {
      cellWidth: pxToTableWidth(176)
    };

    styles[priceIndexes[1]] = {
      cellWidth: pxToTableWidth(207)
    };
  }

  if (priceIndexes.length >= 3) {
    const priceWidth =
      PRICE_AREA_WIDTH / priceIndexes.length;

    priceIndexes.forEach(index => {
      styles[index] = {
        cellWidth: pxToTableWidth(priceWidth)
      };
    });
  }

  return styles;
}
function drawCategoryBlock(doc, categoryTitle, languageFont = null, selLng = '') {
  const x = CATEGORY_CARD_X;
  const y = CATEGORY_CARD_Y;
  const w = CATEGORY_CARD_W;
  const h = CATEGORY_CARD_H;
  const r = CATEGORY_CARD_RADIUS;

  const supportsGState =
    typeof doc.GState === 'function' &&
    typeof doc.setGState === 'function';

  // 1. Slight transparent fill
  if (supportsGState) {
    doc.setGState(new doc.GState({ opacity: CATEGORY_CARD_OPACITY }));
  }

  doc.setFillColor(...CATEGORY_CARD_FILL);
  doc.roundedRect(x, y, w, h, r, r, 'F');

  if (supportsGState) {
    doc.setGState(new doc.GState({ opacity: 1 }));
  }

  // 2. Soft white border
  doc.setDrawColor(...CATEGORY_CARD_BORDER);
  doc.setLineWidth(0.25);
  doc.roundedRect(x, y, w, h, r, r, 'S');

  // 3. Price List
  doc.setTextColor(0, 0, 0);
  doc.setFont(languageFont || 'GeologicaRegular', 'normal');
  doc.setFontSize(CATEGORY_PRICE_LIST_SIZE);
  doc.text('Price List', x + 12, y + 10);

  // 4. Category title
  doc.setFont(languageFont || 'GeologicaMedium', 'normal');
  // const categoryTitleSize = selLng === 'fr' && categoryTitle === 'Produits de Comblement & Revitalisation' ? 21 : CATEGORY_TITLE_SIZE;
  const normalizedCategoryTitle =
    String(categoryTitle || '').trim();

  const categoryTitleSize =
    selLng === 'fr' &&
    normalizedCategoryTitle.includes('Produits de Comblement') ? 21
    : selLng === 'por' && normalizedCategoryTitle.includes('Cosméticos e Máscaras') ? 18 : CATEGORY_TITLE_SIZE;

  doc.setFontSize(categoryTitleSize);
  doc.text(categoryTitle, x + 12, y + 22);

  // 5. Logo on the right - keep aspect ratio
  const logoMaxWidth = 24;
  const logoMaxHeight = 14;

  const logoProps = doc.getImageProperties(koru_logo);
  const logoRatio = logoProps.width / logoProps.height;

  let logoWidth = logoMaxWidth;
  let logoHeight = logoWidth / logoRatio;

  if (logoHeight > logoMaxHeight) {
    logoHeight = logoMaxHeight;
    logoWidth = logoHeight * logoRatio;
  }

  const logoX = x + w - 10 - logoWidth;
  const logoY = y + (h - logoHeight) / 2;

  doc.addImage(
    koru_logo,
    'PNG',
    logoX,
    logoY,
    logoWidth,
    logoHeight
  );
}
function lerp(start, end, t) {
  return start + (end - start) * t;
}

let pageGradientImageCache = null;

function createPageGradientImage() {
  if (pageGradientImageCache) {
    return pageGradientImageCache;
  }

  const canvas = document.createElement('canvas');

  canvas.width = 1200;
  canvas.height = 900;

  const ctx = canvas.getContext('2d');

  // -----------------------------------
  // 1. Horizontal green -> blue
  // -----------------------------------
  const horizontalGradient =
    ctx.createLinearGradient(
      0,
      0,
      canvas.width,
      0
    );

  horizontalGradient.addColorStop(
    0,
    PAGE_BG_GREEN
  );

  horizontalGradient.addColorStop(
    0.5,
    PAGE_BG_GREEN
  );

  horizontalGradient.addColorStop(
    0.7,
    PAGE_BG_BLUE
  );

  horizontalGradient.addColorStop(
    1,
    PAGE_BG_BLUE
  );

  ctx.fillStyle = horizontalGradient;

  ctx.fillRect(
    0,
    0,
    canvas.width,
    canvas.height
  );

  // -----------------------------------
  // 2. Lighten the top slightly
  //    and fade completely to white
  // -----------------------------------
  const verticalFade =
    ctx.createLinearGradient(
      0,
      0,
      0,
      canvas.height
    );

  verticalFade.addColorStop(
    0,
    `rgba(255,255,255,${PAGE_GRADIENT_TOP_LIGHTNESS})`
  );

  verticalFade.addColorStop(
    0.35,
    'rgba(255,255,255,0.38)'
  );

  verticalFade.addColorStop(
    0.7,
    'rgba(255,255,255,0.75)'
  );

  verticalFade.addColorStop(
    1,
    'rgba(255,255,255,1)'
  );

  ctx.fillStyle = verticalFade;

  ctx.fillRect(
    0,
    0,
    canvas.width,
    canvas.height
  );

  pageGradientImageCache =
    canvas.toDataURL('image/jpeg', 0.9);

  return pageGradientImageCache;
}

function drawPageGradientBackground(doc) {
  const pageWidth =
    doc.internal.pageSize.getWidth();

  const pageHeight =
    doc.internal.pageSize.getHeight();

  const gradientImage =
    createPageGradientImage();

  // Gradient area
  doc.addImage(
    gradientImage,
    'JPEG',
    0,
    0,
    pageWidth,
    PAGE_GRADIENT_END_Y
  );

  // Everything below gradient is pure white
  doc.setFillColor(255, 255, 255);

  doc.rect(
    0,
    PAGE_GRADIENT_END_Y,
    pageWidth,
    pageHeight - PAGE_GRADIENT_END_Y,
    'F'
  );
}
function hexToRgb(hex) {
  const value = hex.replace('#', '');

  return [
    parseInt(value.substring(0, 2), 16),
    parseInt(value.substring(2, 4), 16),
    parseInt(value.substring(4, 6), 16)
  ];
}

function getPageGradientColorAtPoint(
  doc,
  x,
  y
) {
  // Below gradient = pure white
  if (y >= PAGE_GRADIENT_END_Y) {
    return [255, 255, 255];
  }

  const pageWidth =
    doc.internal.pageSize.getWidth();

  const green = hexToRgb(PAGE_BG_GREEN);
  const blue = hexToRgb(PAGE_BG_BLUE);

  const xRatio = Math.max(
    0,
    Math.min(1, x / pageWidth)
  );

  let baseColor;

  // Same stops as createPageGradientImage():
  // 0     green
  // 0.5   green
  // 0.7   blue
  // 1     blue

  if (xRatio <= 0.5) {
    baseColor = green;
  } else if (xRatio <= 0.7) {
    const t =
      (xRatio - 0.5) / 0.2;

    baseColor = [
      Math.round(lerp(green[0], blue[0], t)),
      Math.round(lerp(green[1], blue[1], t)),
      Math.round(lerp(green[2], blue[2], t))
    ];
  } else {
    baseColor = blue;
  }

  // Same vertical white fade as background
  const yRatio = Math.max(
    0,
    Math.min(1, y / PAGE_GRADIENT_END_Y)
  );

  let whiteOpacity;

  if (yRatio <= 0.35) {
    whiteOpacity = lerp(
      PAGE_GRADIENT_TOP_LIGHTNESS,
      0.38,
      yRatio / 0.35
    );
  } else if (yRatio <= 0.7) {
    whiteOpacity = lerp(
      0.38,
      0.75,
      (yRatio - 0.35) / 0.35
    );
  } else {
    whiteOpacity = lerp(
      0.75,
      1,
      (yRatio - 0.7) / 0.3
    );
  }

  return baseColor.map(color =>
    Math.round(
      color * (1 - whiteOpacity) +
      255 * whiteOpacity
    )
  );
}
// function getPageBackgroundColorAtY(y) {
//   if (y >= PAGE_GRADIENT_END_Y) {
//     return PAGE_BG_BOTTOM_COLOR;
//   }

//   if (y <= PAGE_GRADIENT_START_Y) {
//     return PAGE_BG_TOP_COLOR;
//   }

//   const t =
//     (y - PAGE_GRADIENT_START_Y) /
//     (PAGE_GRADIENT_END_Y - PAGE_GRADIENT_START_Y);

//   return [
//     Math.round(
//       lerp(
//         PAGE_BG_TOP_COLOR[0],
//         PAGE_BG_BOTTOM_COLOR[0],
//         t
//       )
//     ),
//     Math.round(
//       lerp(
//         PAGE_BG_TOP_COLOR[1],
//         PAGE_BG_BOTTOM_COLOR[1],
//         t
//       )
//     ),
//     Math.round(
//       lerp(
//         PAGE_BG_TOP_COLOR[2],
//         PAGE_BG_BOTTOM_COLOR[2],
//         t
//       )
//     )
//   ];
// }
/**
 * Calculates the total number of columns in a table header row, accounting for colSpan properties.
 *
 * Iterates through each cell in the header row array. If a cell is an object with a `colSpan` property,
 * its value is added to the total. Otherwise, each cell is counted as a single column.
 *
 * @param {Array<Object|any>} headerRow - The array representing a table header row, where each cell may be an object with a `colSpan` property or a simple value.
 * @returns {number} The total number of columns represented by the header row.
 */
function getTotalCols(headerRow) {
  return headerRow.reduce((sum, cell) => {
    if (typeof cell === 'object' && cell.colSpan) {
      return sum + cell.colSpan;
    }
    return sum + 1;
  }, 0);
}
export default function PriceList({ profileData, loading }) {
  const animatedComponents = makeAnimated();
  const {
    data = [],
    isLoading,
    isFetching,
    error,
    refetch
  } = pricesApi.useFetchAllPricesQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false
  });

  const [addProduct, { isLoading: isSubmitting }] = pricesApi.useAddPriceMutation();
  const [updateProduct, { isLoading: isUpdating }] = pricesApi.useUpdatePriceMutation();
  const [trackPdfDownload] = pricesApi.useTrackPdfDownloadMutation();
  const [deleteProduct, { isLoading: isDeleting }] = pricesApi.useDeletePriceMutation();
  const [isExporting, setIsExporting] = useState(false);
  // const [triggerGetFont, { isLoading: isFontLoading }] = pricesApi.useGetFontMutation();

  const [languages, setLanguagesData] = useState([]);
  const [countries, setCountriesData] = useState([]);
  const [categories, setCategoriesData] = useState([]);
  const [sub_categories, setSubCategoriesData] = useState([]);
  const [products, setProductsData] = useState([]);
  const [users, setUsersData] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isProductEdit, setIsProductEdit] = useState(false);
  const [isPriceEdit, setIsPriceEdit] = useState(false);
  const [isRegPriceEdit, setIsRegPriceEdit] = useState(false);

  const [selProduct, setSelectedProduct] = useState([]);
  const [latestSortNo, setLatestSortNo] = useState(false);
  const [selPic, setSelPic] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [lng, setLng] = useState('eng');
  const [region, setRegion] = useState('');
  const [regions, setRegions] = useState({
    expDef: '',
    expCont: '',
    expCntr: '',
    expSt: '',
    expCt: '',
    expLng: ''
  });
  const isDisabled = (...keys) => keys.some((k) => regions[k] !== '');
  const [filters, setNoDbFilters] = useState([]);

  const { progress, step, visible } = useLoadingProgress({
    isLoading,
    isFetching,
    isSubmitting,
    isUpdating,
    isDeleting
  });
  useEffect(() => {
    if (!isLoading && data.products) {
      setLanguagesData(JSON.parse(JSON.stringify(data.languages)));
      setCountriesData(JSON.parse(JSON.stringify(data.countries)));
      setCategoriesData(JSON.parse(JSON.stringify(data.categories)));
      setSubCategoriesData(JSON.parse(JSON.stringify(data.sub_categories)));
      setProductsData(JSON.parse(JSON.stringify(data.products)));
      setUsersData(JSON.parse(JSON.stringify(data.users)));
    }
  }, [data, isLoading]);

  const [cardHeight, setCardHeight] = useState(0);
  const cardRef = useRef(null);
  useEffect(() => {
    if (!cardRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const newHeight = entry.contentRect.height;
        if (newHeight > 0) {
          setCardHeight(newHeight);
          // const el = document.querySelector('.react-bootstrap-table');
          // if (el) {
          //   el.setAttribute('style', `height: calc(${newHeight}px - 74px); overflow: auto;`);
          // }
        }
      }
    });
    resizeObserver.observe(cardRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!cardHeight) return;
    // try immediately
    const apply = () => {
      const el = document.querySelector('.react-bootstrap-table');
      if (!el) return false;
      el.style.height = `calc(${cardHeight}px - 150px)`;
      el.style.overflow = 'auto';
      return true;
    };

    if (apply()) return; // applied on first try

    // otherwise, wait for the table to appear
    const mo = new MutationObserver(() => {
      if (apply()) mo.disconnect();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => mo.disconnect();
  }, [cardHeight, categories]);

  useEffect(() => {
    const fltrLng = filters?.languages?.[0] || 'eng';
    setLng(fltrLng);
  }, [filters]);

  const [modalNewProduct, setModalNewProduct] = useState(false);
  const [modalEditPrice, setModalEditPrice] = useState(false);
  const [modalEditRegPrice, setModalEditRegPrice] = useState(false);
  const [modalExportPDF, setModalExportPDF] = useState(false);
  const [modalBulkEdit, setModalBulkEdit] = useState(false);

  /**
   * Toggles the visibility state of the "New Product" modal.
   * Uses the previous state to switch between open and closed.
   * Intended to be used as an event handler for opening or closing the modal.
   */
  const toggleNewProductModal = useCallback(() => {
    setModalNewProduct((prev) => !prev);
  }, []);
  /**
   * Toggles the visibility state of the Edit Price modal.
   * Uses the previous state to switch between open and closed.
   * Intended to be used as a callback for modal open/close actions.
   */
  const toggleEditPriceModal = useCallback(() => {
    setModalEditPrice((prev) => !prev);
  }, []);
  /**
   * Toggles the visibility state of the "Edit Regular Price" modal.
   * Uses the previous state to switch between open and closed.
   * Intended to be used as an event handler for opening or closing the modal.
   */
  const toggleEditRegPriceModal = useCallback(() => {
    setModalEditRegPrice((prev) => !prev);
  }, []);
  /**
   * Toggles the visibility state of the Export PDF modal.
   * Uses the previous state to switch between open and closed.
   * Intended to be used as an event handler for opening or closing the modal.
   */
  const toggleExportPDFModal = useCallback(() => {
    setModalExportPDF((prev) => !prev);
  }, []);
  /**
   * Toggles the visibility state of the bulk edit modal.
   * Uses a callback to invert the current modal state.
   */
  const toggleBulkEditModal = useCallback(() => {
    setModalBulkEdit((prev) => !prev);
  }, []);
  /**
   * Prepares and initializes state for the price list page based on the provided mode and data.
   *
   * This function sets up various UI states and flags depending on the operation mode,
   * such as creating a new product, editing a product, editing a price, editing a regular price,
   * or performing bulk operations. It also handles selection of products, pictures, files,
   * and resets relevant form values and error states for bulk actions.
   *
   * @param {Object} params - The parameters for preparing data.
   * @param {'create'|'editProduct'|'editPrice'|'editRegPrice'|'bulk'} params.mode - The current operation mode.
   * @param {Array} [params.product=[]] - The product(s) to be selected or edited.
   * @param {*} [params.pic=null] - The picture or file associated with the product.
   */
  const prepareData = ({ mode, product = [], pic = null }) => {
    const isCreate = mode === 'create';
    const isEditProduct = mode === 'editProduct';
    const isEditPrice = mode === 'editPrice';
    const isEditReg = mode === 'editRegPrice';
    const isBulk = mode === 'bulk';
    const isEdit = !isCreate && !isBulk;

    // Common
    setSelectedProduct(product);
    setActiveTab(1);
    setLatestSortNo(1);

    // Steps: start fresh on create; keep first step done on product edit; otherwise leave empty
    setPassedSteps(isEditProduct ? [1] : []);

    // Picture/files
    setSelPic(pic);
    setSelectedFiles(pic ? [pic] : []);

    // Flags (keeping your existing booleans)
    setIsProductEdit(isEditProduct);
    setIsPriceEdit(isEditPrice);
    setIsRegPriceEdit(isEditReg);
    setIsEdit(isEdit);

    if (isBulk) {
      // bulk-specific resets
      setShowError(false);
      setFormValues({});
      setSelectedMultiProducts([]);
      setSelectedMultiRegions([]);
    }
  };
  /**
   * Handles the click event for creating a new product.
   * Prepares the necessary data in 'create' mode and toggles the new product modal.
   */
  const handleNewProductClick = () => {
    prepareData({ mode: 'create' });
    toggleNewProductModal();
  };
  /**
   * Handles the click event for editing a product.
   * Prepares the product data for editing and opens the product modal.
   *
   * @param {React.MouseEvent} e - The click event object.
   * @param {Object} product - The product object to be edited.
   */
  const handleEditProductClick = (e, product) => {
    prepareData({ mode: 'editProduct', product, pic: product?.pic ?? null });
    toggleNewProductModal();
  };
  /**
   * Handles the click event for editing a product's price.
   * Prepares the necessary data for editing, including setting the mode and product,
   * and then toggles the edit price modal to display the editing interface.
   *
   * @param {React.MouseEvent} e - The click event object.
   * @param {Object} product - The product object whose price is to be edited.
   */
  const handleEditPriceClick = (e, product) => {
    prepareData({ mode: 'editPrice', product, pic: null });
    toggleEditPriceModal();
  };
  /**
   * Handles the click event for editing the regular price of a product.
   * Prepares the necessary data and opens the edit regular price modal.
   *
   * @param {Event} e - The click event object.
   * @param {Object} product - The product object whose regular price is to be edited.
   */
  const handleEditRegPriceClick = (e, product) => {
    prepareData({ mode: 'editRegPrice', product, pic: null });
    toggleEditRegPriceModal();
  };
  /**
   * Handles the click event for exporting the price list as a PDF.
   * Toggles the visibility of the export PDF modal dialog.
   */
  const handleExportPDFClick = () => {
    toggleExportPDFModal();
  };

  const [formValues, setFormValues] = useState({});
  const [selectedMultiProducts, setSelectedMultiProducts] = useState([]);
  const [selectedMultiRegions, setSelectedMultiRegions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [inputValueSelect, setInputValueSelect] = useState('');
  const [rusdFilled, setRusdFilled] = useState('');
  const [showError, setShowError] = useState(false);
  const [showRegionsError, setShowRegionsError] = useState(false);
  const [showPriceError, setShowPriceError] = useState(false);
  /**
   * Handles the bulk edit action for a product.
   * Prepares the necessary data for bulk editing, sorts products by category and region,
   * and opens the bulk edit modal. If sorting operations are heavy, consider triggering
   * them inside the bulk modal via useEffect.
   *
   * @param {React.MouseEvent} e - The click event object.
   * @param {Object} product - The product object to be edited in bulk.
   */
  const handleBulkEditProductClick = (e, product) => {
    prepareData({ mode: 'bulk' });
    // if these are heavy, kick them off inside the bulk modal via useEffect
    sortProductsByCategory();
    sortProductsByRegion();
    toggleBulkEditModal();
  };
  /**
   * CustomOption is a React functional component used to render a selectable option
   * with a checkbox inside a dropdown menu, typically for multi-select scenarios.
   * It leverages react-select's Option component and displays the option's label
   * alongside a checkbox indicating its selection state.
   *
   * @param {Object} props - The properties passed to the component.
   * @param {Object} props.data - The data object representing the option.
   * @param {Function} props.selectOption - Callback to select or deselect the option.
   * @param {boolean} props.isSelected - Indicates whether the option is currently selected.
   * @returns {JSX.Element} The rendered option component with a checkbox and label.
   */
  const CustomOption = (props) => {
    const { data, selectOption, isSelected } = props;
    return (
      <components.Option {...props}>
        <input type='checkbox' checked={isSelected} onChange={() => selectOption(data)} style={{ marginRight: 8 }} />
        {data.label}
      </components.Option>
    );
  };
  /**
   * Renders a custom group heading for product categories with a label and a "select all" checkbox.
   *
   * @param {Object} props - The properties passed to the component.
   * @param {Object} props.data - The data object containing group information.
   * @param {string} props.data.label - The label for the group heading.
   * @param {Object} props.selectProps - Additional props for selection handling.
   * @returns {JSX.Element} The rendered group heading with a label and checkbox.
   */
  const CustomGroupHeadingProduct = (props) => {
    const { label } = props.data;
    return (
      <div style={{ marginLeft: '10px', marginRight: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <strong>{label}</strong>
        <input type='checkbox' onChange={() => handleCategorySelectAll(label, props.selectProps)} style={{ marginLeft: 'auto' }} />
      </div>
    );
  };
  /**
   * CustomGroupHeadingRegion is a React functional component that renders a group heading
   * with a label and a "select all" checkbox for a specific region. The checkbox triggers
   * the handleRegionSelectAll function with the region label when changed.
   *
   * @param {Object} props - The props for the component.
   * @param {Object} props.data - The data object containing the region information.
   * @param {string} props.data.label - The label for the region group heading.
   * @returns {JSX.Element} The rendered group heading with a label and checkbox.
   */
  const CustomGroupHeadingRegion = (props) => {
    const { label } = props.data;
    return (
      <div style={{ marginLeft: '10px', marginRight: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <strong>{label}</strong>
        <input type='checkbox' onChange={() => handleRegionSelectAll(label)} style={{ marginLeft: 'auto' }} />
      </div>
    );
  };
  /**
   * Sorts products into categories and formats them for selection options.
   *
   * Iterates through the provided categories, filters products belonging to each category,
   * and maps them into option objects with descriptive labels. The label formatting varies
   * depending on the category, including additional product details for specific categories.
   * The resulting options are sorted alphabetically by label within each category.
   * Finally, updates the product options state with the grouped and sorted options.
   *
   * @function
   */
  const sortProductsByCategory = () => {
    let productOptions = [];
    categories.forEach((category) => {
      let categoryOptions = products
        .filter((product) => product.category.catNameLng?.catName === category.catNameLng?.catName)
        .map((product) => ({
          label:
            product.languages.eng.title +
            (['PDO Threads', 'PLLA Threads', 'PCL Threads', 'Mesoneedles & Cannulas'].includes(category.catNameLng?.catName)
              ? ' ' + product.languages.eng.composition
              : category.catNameLng?.catName === 'COG Threads'
              ? ' ' +
                product.languages.eng.needle_type +
                ' ' +
                product.languages.eng.needle_gage +
                ' ' +
                product.languages.eng.needle_mm +
                ' ' +
                product.languages.eng.thread_type +
                ' ' +
                product.languages.eng.thread_gage +
                ' ' +
                product.languages.eng.thread_mm
              : ''),
          value: product._id,
          category: category.catNameLng?.catName
        }))
        .sort((a, b) => a.label.localeCompare(b.label)); // Sorting by label alphabetically

      productOptions.push({
        label: category.catNameLng?.catName,
        options: categoryOptions
      });
    });
    setProductOptions(productOptions);
  };
  /**
   * Groups countries by their region and formats the data for use with react-select.
   * Each region becomes a group label containing its respective countries as options.
   * The formatted region options are then set using the setRegionOptions state updater.
   *
   * @function
   * @returns {void}
   */
  const sortProductsByRegion = () => {
    let regionOptions = [];
    // Group countries by region
    const regionMap = {};
    countries.forEach((country) => {
      if (!regionMap[country.region]) {
        regionMap[country.region] = [];
      }
      regionMap[country.region].push({
        label: country.cntry_name, // Display country name
        value: country.cntry_code, // Store country code as value
        region: country.region
      });
    });
    // Convert to react-select format
    Object.keys(regionMap).forEach((region) => {
      regionOptions.push({
        label: region, // Region name as group label
        options: regionMap[region] // Countries under this region
      });
    });
    setRegionOptions(regionOptions);
  };
  /**
   * Handles the selection or deselection of all visible products within a category based on the current search value.
   * If all visible products are already selected, it deselects them; otherwise, it selects all visible products.
   *
   * @param {string} category - The name of the category whose products are being selected or deselected.
   * @param {Object} selectProps - The properties of the select component, including the current input value for search.
   */
  const handleCategorySelectAll = (category, selectProps) => {
    const searchValue = selectProps.inputValue.toLowerCase(); // Get current search value
    let updatedSelected = [...selectedMultiProducts];
    // Get the full category product list
    const categoryProducts = productOptions.find((group) => group.label === category)?.options || [];
    // Filter only visible products (based on search value)
    const visibleProducts = categoryProducts.filter((prod) => prod.label.toLowerCase().includes(searchValue));
    // Check if all visible products are selected
    const allVisibleSelected = visibleProducts.every((prod) => selectedMultiProducts.some((sel) => sel.value === prod.value));
    if (allVisibleSelected) {
      // Deselect only visible products
      updatedSelected = updatedSelected.filter((sel) => !visibleProducts.some((prod) => prod.value === sel.value));
    } else {
      // Select only visible products
      visibleProducts.forEach((prod) => {
        if (!updatedSelected.some((sel) => sel.value === prod.value)) {
          updatedSelected.push(prod);
        }
      });
    }
    setSelectedMultiProducts(updatedSelected);
  };
  /**
   * Handles the selection or deselection of all products within a specified region.
   * If all products in the region are already selected, it deselects them; otherwise, it selects all.
   *
   * @param {string} region - The label of the region whose products are to be selected or deselected.
   */
  const handleRegionSelectAll = (region) => {
    let updatedSelected = [...selectedMultiRegions];
    const regionProducts = regionOptions.find((group) => group.label === region)?.options || [];
    const allSelected = regionProducts.every((prod) => selectedMultiRegions.some((sel) => sel.value === prod.value));
    if (allSelected) {
      updatedSelected = updatedSelected.filter((sel) => !regionProducts.some((prod) => prod.value === sel.value));
    } else {
      regionProducts.forEach((prod) => {
        if (!updatedSelected.some((sel) => sel.value === prod.value)) {
          updatedSelected.push(prod);
        }
      });
    }
    setSelectedMultiRegions(updatedSelected);
  };
  /**
   * Handles the change event for the region filter input.
   * Updates the selected region state based on user input.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event triggered by the filter input.
   */
  const handleFilterChange = (e) => {
    setRegion(e.target.value);
  };
  /**
   * Handles changes to region-related input fields.
   * Updates the `regions` state based on the input's name and value.
   * If the input name is 'expDef', it also sets the 'expLng' property to 'eng' when a value is provided,
   * or resets it to an empty string if the value is empty.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the input field.
   */
  const handleRegionsChange = (e) => {
    const { name, value } = e.target;
    if (name === 'expDef') {
      setRegions((prev) => ({ ...prev, expLng: value === '' ? '' : 'eng' }));
    }
    setRegions((prev) => ({ ...prev, [name]: value }));
  };
  /**
   * Renders a custom filter UI component for selecting a region from a sorted list of countries.
   * The component includes a styled select input populated with country options and an icon badge.
   *
   * @constant
   * @type {JSX.Element}
   * @param {Object[]} countries - Array of country objects to populate the select options.
   * @param {string} countries[].cntry_code - The country code used as the option value.
   * @param {string} countries[].cntry_name - The country name displayed in the option.
   * @param {function} handleFilterChange - Callback function triggered when the selected region changes.
   * @returns {JSX.Element} The rendered filter UI component.
   */
  const filterCustomContent = (
    <FormGroup className='ms-1'>
      <InputGroup>
        <Input
          name='region'
          type='select'
          onChange={handleFilterChange}
          style={{
            backgroundColor: 'white',
            color: 'black !important',
            borderColor: '#ced4da',
            borderRadius: '8px',
            color: '#000'
          }}
        >
          <option value=''>Region</option>
          {countries
            .sort((a, b) => a.cntry_name.localeCompare(b.cntry_name))
            .map((cntry, index) => (
              <option key={index} className='fw-bold fs-5' value={cntry.cntry_code}>
                {cntry.cntry_name}
              </option>
            ))}
        </Input>
        <InputGroupText style={{ backgroundColor: 'white', border: 'none', position: 'absolute', right: '5px', top: '2px' }}>
          <BadgePlus size={20} color='black' />
        </InputGroupText>
      </InputGroup>
    </FormGroup>
  );

  const modalBodyRef = useRef(null);
  /**
   * Scrolls the modal body element to the top.
   * If the modal body reference is available, sets its scroll position to the top.
   */
  const scrollToTop = () => {
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTop = 0;
    }
  };
  const [activeTab, setActiveTab] = useState(1);
  const [passedSteps, setPassedSteps] = useState([1]);
  /**
   * Toggles the active tab to the specified tab number if it is different from the current active tab
   * and within the valid range (1 to 6). Also adds the tab number to the list of passed steps.
   *
   * @param {number} tab - The tab number to activate.
   */
  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      if (tab >= 1 && tab <= 6) {
        setActiveTab(tab);
        setPassedSteps((prevSteps) => [...prevSteps, tab]);
      }
    }
  };

  const handleCategoryChange = (e) => {
    let latestSortNo = -1;
    for (const product of products) {
      if (product.category._id === e.target.value) {
        // Check if the product belongs to the desired category
        latestSortNo = Math.max(latestSortNo, product.sortNo);
      }
    }
    setLatestSortNo(latestSortNo + 1);
  };

  const handleAcceptedFiles = (files) => {
    files.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        acceptedFiles: 'image/*',
        formattedSize: formatBytes(file.size)
      })
    );
    for (var i = 0; i < files.length; i++) {
      let file = files[i];
      if (file !== null) {
        if (file.preview) {
          getBase64(file).then((file_url) => {
            const updatedFile = [{ file: file_url }];
            setSelPic(updatedFile);
          });
        }
      }
    }
    setSelectedFiles(files);
  };
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };
  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };
  /**
   * remove product from price list
   * @param {*} option
   * @param {*} product
   */
  const hideProduct = (option, product) => {
    product.hidden = option === 'hide' ? true : false;
    try {
      updateProduct(product).unwrap();
      showToast('Product is updated');
    } catch (error) {
      console.error('Failed to update row:', error);
      showToast('Failed to update row', 'error');
    }
  };
  /**
   * mark product as assortment product
   * @param {*} option
   * @param {*} product
   */
  const assProduct = (option, product) => {
    product.ass_product = option === 'ass' ? true : false;
    try {
      updateProduct(product).unwrap();
      showToast('Product is updated');
    } catch (error) {
      console.error('Failed to update row:', error);
      showToast('Failed to update row', 'error');
    }
  };
  /**
   * Handles the submission of the first step in the product creation or editing process.
   * Constructs a new product object based on the provided form values, including category, subcategory,
   * image, pricing details, product attributes, and language-specific information.
   * If editing an existing product, updates its English language fields; otherwise, initializes them.
   * Updates the selected product state, advances the active tab, and scrolls to the top of the page.
   *
   * @param {Event} e - The form submission event.
   * @param {Object} values - The form values containing product details.
   */
  const submitFirstStep = (e, values) => {
    const newValues = {
      category: { _id: values['category'] },
      subCategory: values['subCategory'] === '' ? null : values['subCategory'],
      pic: selPic && selPic !== null ? selPic[0].file : null,
      prices: {
        usd: {
          price_r: values['rusd'] || null,
          price_w: values['wusd'] || null,
          price_w_add: values['wusd_add'] || null
        },
        eur: {
          price_r: values['reur'] || null,
          price_w: values['weur'] || null,
          price_w_add: values['weur_add'] || null
        }
      },
      r_amount: values['r_amount'] || null,
      w_amount: values['w_amount'] || null,
      w_amount_add: values['w_amount_add'] || '',
      best_seller: values['best_seller'],
      new_product: values['new_product'],
      ass_product: values['ass_product'],
      hidden: isEdit ? selProduct.hidden : false,
      sortNo: parseInt(values['sortNo']),
      nomenclatureCode: values['nomenclatureCode'],
      oldSortNo: isEdit ? selProduct.sortNo : latestSortNo
    };
    if (isEdit) {
      selProduct.languages.eng = {
        title: values['title'] || null,
        composition: values['composition'] || null,
        description: values['description'] || null,
        needle_type: values['needle_type'] || null,
        needle_gage: values['needle_gage'] || null,
        needle_mm: values['needle_mm'] || null,
        thread_type: values['thread_type'] || null,
        thread_gage: values['thread_gage'] || null,
        thread_mm: values['thread_mm'] || null,
        display: true
      };
    } else {
      newValues.languages = {
        eng: {
          title: values['title'] || null,
          composition: values['composition'] || null,
          description: values['description'] || null,
          needle_type: values['needle_type'] || null,
          needle_gage: values['needle_gage'] || null,
          needle_mm: values['needle_mm'] || null,
          thread_type: values['thread_type'] || null,
          thread_gage: values['thread_gage'] || null,
          thread_mm: values['thread_mm'] || null,
          display: true
        }
      };
      newValues.regions = {};
    }

    setSelectedProduct((prev) => ({ ...prev, ...newValues }));
    toggleTab(activeTab + 1);
    scrollToTop();
  };
  const submitSecondStep = (e, values) => {
    // Create a map to hold languages and their properties
    let languagesMap = {};
    // Iterate over each key in the values object
    for (let key in values) {
      if (values.hasOwnProperty(key)) {
        // Split the key to get the field name and language code (e.g., "title" and "ru" from "title_ru")
        let [field, langCode] = [key.slice(0, key.lastIndexOf('_')), key.slice(key.lastIndexOf('_') + 1)];
        // Initialize the language entry in the map if it doesn't exist
        if (!languagesMap[langCode]) {
          languagesMap[langCode] = {};
        }
        // Add the field to the language entry only if it's not an empty string
        if (values[key]) {
          languagesMap[langCode][field] = values[key];
          languagesMap[langCode]['display'] = true;
        }
      }
    }
    // Merge the languagesMap into selProduct.languages
    for (let langCode in languagesMap) {
      if (Object.keys(languagesMap[langCode]).length > 0) {
        selProduct.languages[langCode] = languagesMap[langCode];
      }
    }
    selProduct.languages.eng.display = true;
    toggleTab(activeTab + 1);
    setSelectedProduct(selProduct);
    scrollToTop();
  };
  const handleNewProductSubmit = (e, values) => {
    // Create a map to hold regions and their properties
    let regionsMap = {};
    // Iterate over each key in the values object
    for (let key in values) {
      if (values.hasOwnProperty(key)) {
        // Split the key to get the field name and region code (e.g., "name" and "az" from "name_az")
        let [field, cntryCode] = [key.slice(0, key.lastIndexOf('_')), key.slice(key.lastIndexOf('_') + 1)];
        // Initialize the region entry in the map if it doesn't exist
        if (!regionsMap[cntryCode]) {
          regionsMap[cntryCode] = {};
        }
        // Add the field to the region entry only if it's not an empty string
        if (values[key]) {
          regionsMap[cntryCode][field] = values[key];
        }
      }
    }
    if (!selProduct.regions) {
      selProduct.regions = {};
    }
    // Merge the regionsMap into selProduct.regions
    for (let cntryCode in regionsMap) {
      const region = regionsMap[cntryCode];

      // Exclude regions where both price_r and price_w are empty
      const priceR = region['price_r'] || '';
      const priceW = region['price_w'] || '';

      // If both price_r and price_w are not empty, merge into selProduct.regions
      if (priceR.trim() || priceW.trim()) {
        selProduct.regions[cntryCode] = region;
      }
    }
    setSelectedProduct(selProduct);
    if (isPriceEdit) {
      selProduct.r_amount = values.r_amount;
      selProduct.w_amount = values.w_amount;
      selProduct.w_amount_add = values.w_amount_add;
      selProduct.prices.usd = {
        price_r: values.rusd,
        price_w: values.wusd,
        price_w_add: values.wusd_add
      };
      selProduct.prices.eur = {
        price_r: values.reur,
        price_w: values.weur,
        price_w_add: values.weur_add
      };
    }
    if (isEdit) {
      try {
        updateProduct(selProduct).unwrap();
        showToast('Product is updated');
      } catch (error) {
        console.error('Failed to update row:', error);
        showToast('Failed to update row', 'error');
      }
    } else {
      try {
        addProduct(selProduct).unwrap();
        showToast('Product is added');
      } catch (error) {
        console.error('Failed to update row:', error);
        showToast('Failed to update row', 'error');
      }
      toggleNewProductModal();
    }
    if (isProductEdit) {
      toggleNewProductModal();
    }
    if (isPriceEdit) {
      toggleEditPriceModal();
    }
    if (isRegPriceEdit) {
      toggleEditRegPriceModal();
    }
  };
  const handleMultiProductsSelect = (selectedMultiProducts) => {
    if (selectedMultiProducts.length === 1) {
      const product = products.find((prod) => prod._id === selectedMultiProducts[0].value);
      setSelectedProduct(product);
    } else {
      setSelectedProduct([]);
    }
    setSelectedMultiProducts(selectedMultiProducts);
  };
  const handleMultiRegionsSelect = (selectedMultiRegions) => {
    setSelectedMultiRegions(selectedMultiRegions);
  };
  /**
   * Handles selection changes for an input component.
   * Updates the input value state only if the action is not "set-value" or "menu-close".
   *
   * @param {any} newValue - The new value selected by the user.
   * @param {Object} actionMeta - Metadata about the selection action.
   * @param {string} actionMeta.action - The type of action performed (e.g., "set-value", "menu-close").
   */
  const handleInputSelect = (newValue, actionMeta) => {
    if (actionMeta.action !== 'set-value' && actionMeta.action !== 'menu-close') {
      setInputValueSelect(newValue);
    }
  };
  /**
   * Handles changes to the RUSD input field.
   * Updates the local state for the filled RUSD value and synchronizes it with the form values.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event triggered by the input field.
   */
  const onChangeRUSD = (event) => {
    const value = event.target.value;
    setRusdFilled(value);
    setFormValues((formValues) => ({ ...formValues, rusd: value }));
  };
  /**
   * Handles changes to form inputs and updates the form values state accordingly.
   * For checkbox inputs, sets the value based on the checked property; otherwise, uses the input value.
   * Specially handles the "assign_default" field: if checked or undefined, clears "price_r";
   * otherwise, sets "price_r" to a specific message.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the form input.
   */
  const handleFormChange = (e) => {
    const { name, type, checked, value } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    const updatedFormValues = {
      ...formValues,
      [name]: newValue
    };

    // When assign_default changes, update price_r dynamically
    if (name === 'assign_default' || name === 'assign_default_eur') {
      if (newValue === true || newValue === undefined) {
        updatedFormValues['price_r'] = '';
      } else {
        updatedFormValues['price_r'] = 'You can purchase from our official distributor (Keep it empty if you want to remove product from the region)';
      }
    }
    setFormValues(updatedFormValues);
  };
  /**
   * Handles the submission of the bulk edit form for products.
   * Validates selected products, regions, and price fields, then constructs
   * updated product data based on form values and selected options.
   * Updates products, prepares data for bulk operations, and closes the bulk edit modal.
   *
   * @param {Event} e - The form submission event.
   * @param {Object} vals - Additional values from the bulk edit form, including flags and price fields.
   */
  const handleBulkEditSubmit = (e, vals) => {
    const values = formValues;
    e.preventDefault();
    if (!selectedMultiProducts || selectedMultiProducts.length === 0) {
      setShowError(true);
      return;
    }
    if (activeTab === 3 && selectedMultiRegions.length === 0) {
      setShowRegionsError(true);
      return;
    }
    if (vals?.price_r && vals?.price_r.includes('(Keep it empty if you want to remove product from the region)')) {
      setShowPriceError(true);
      return;
    }
    const updatedProducts = selectedMultiProducts.map((product) => {
      const productData = { _id: product.value };
      ['ass_product', 'hidden', 'best_seller', 'new_product', 'r_amount', 'w_amount', 'w_amount_add'].forEach((field) => {
        if (values[field] !== undefined && values[field] !== '') {
          productData[field] = values[field];
        }
      });
      const prices = {};
      const usd = {};
      if (values.rusd) usd.price_r = values.rusd;
      if (values.wusd) usd.price_w = values.wusd;
      if (Object.keys(usd).length > 0) prices.usd = usd;

      const eur = {};
      if (values.reur) eur.price_r = values.reur;
      if (values.weur) eur.price_w = values.weur;
      if (Object.keys(eur).length > 0) prices.eur = eur;

      if (Object.keys(prices).length > 0) {
        productData.prices = prices;
      }

      if ((selectedMultiRegions.length > 0 && (values.price_r || vals.assign_default)) || (selectedMultiRegions.length > 0 && (values.price_r || vals.assign_default_eur))) {
        const regionMap = {};
        const defaultProduct = products.find((p) => p._id === product.value);

        selectedMultiRegions.forEach((region) => {
          let price_r, price_w;

          if (vals.assign_default && defaultProduct?.prices?.usd) {
            price_r = defaultProduct.prices.usd.price_r || '';
            price_w = defaultProduct.prices.usd.price_w || '';
          } else if (vals.assign_default_eur && defaultProduct?.prices?.eur) {
            price_r = defaultProduct.prices.eur.price_r || '';
            price_w = defaultProduct.prices.eur.price_w || '';
          } else {
            price_r = values.price_r;
            price_w = values.price_r;
          }

          regionMap[region.value] = {
            name: region.label,
            price_r,
            price_w
          };
        });

        productData.regions = regionMap;
      }
      return productData;
    });
    console.log('Updated Products:', updatedProducts);
    try {
      updateProduct(['dist_upd', updatedProducts]).unwrap();
      showToast('Products are updated');
    } catch (error) {
      console.error('Failed to update row:', error);
      showToast('Failed to update rows', 'error');
    }
    prepareData({ mode: 'bulk' });
    toggleBulkEditModal();
  };

  const [addFilters, setAddFilters] = useState({
    includeWAmount: false,
    includeWAmountAdd: false,
    assProducts: false,
    noAssProducts: true
  });
  /**
   * Handles changes to additional filter checkboxes and updates the filter state accordingly.
   *
   * Depending on the checkbox name and its checked state, this function updates the relevant
   * filter properties in the state:
   * - "includeWAmount": Sets `includeWAmount` to the checked value.
   * - "includeWAmountAdd": If checked, sets both `includeWAmount` and `includeWAmountAdd` to true;
   *   if unchecked, sets both to false.
   * - "assProducts": If checked, sets `assProducts` to true and `noAssProducts` to false;
   *   if unchecked, sets `assProducts` to false and `noAssProducts` to true.
   * - "noAssProducts": Sets `noAssProducts` to the checked value and `assProducts` to false.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the filter checkbox.
   */
  const handleAddFilterChange = (e) => {
    const { name, checked } = e.target;

    setAddFilters((prev) => {
      let updated = { ...prev };

      switch (name) {
        case 'includeWAmount':
          // 1. if includeWAmount is checked make includeWAmount true, dont change others
          updated.includeWAmount = checked;
          break;

        case 'includeWAmountAdd':
          // 2. if checked => includeWAmount + includeWAmountAdd true
          //    if unchecked => both false
          if (checked) {
            updated.includeWAmount = true;
            updated.includeWAmountAdd = true;
          } else {
            updated.includeWAmount = false;
            updated.includeWAmountAdd = false;
          }
          break;

        case 'assProducts':
          // 3. if assProducts is checked => assProducts true + noAssProducts false
          //    if unchecked => assProducts false + noAssProducts true
          if (checked) {
            updated.assProducts = true;
            updated.noAssProducts = false;
          } else {
            updated.assProducts = false;
            updated.noAssProducts = true;
          }
          break;

        case 'noAssProducts':
          // 4. if unchecked => just false
          updated.assProducts = false;
          updated.noAssProducts = checked;
          break;

        default:
          break;
      }

      return updated;
    });
  };
  /**
   * Compresses a base64-encoded image by resizing it to a specified maximum width and adjusting its quality.
   * The function returns a Promise that resolves to a compressed base64 JPEG image string.
   * The aspect ratio of the original image is preserved.
   *
   * @param {string} base64Str - The base64-encoded image string to compress.
   * @param {number} [maxWidth=120] - The maximum width of the compressed image in pixels.
   * @param {number} [quality=1] - The quality of the compressed JPEG image (between 0 and 1).
   * @returns {Promise<string>} A Promise that resolves to the compressed base64 JPEG image string.
   */
  const compressBase64Image = (
    base64Str,
    maxWidth = 120,
    quality = 1
  ) => {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        // Never upscale small images
        const targetWidth = Math.min(
          img.width,
          maxWidth
        );

        const scaleFactor =
          targetWidth / img.width;

        const targetHeight = Math.round(
          img.height * scaleFactor
        );

        const canvas =
          document.createElement('canvas');

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const ctx = canvas.getContext('2d');

        // White background because output is JPEG
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(
          0,
          0,
          canvas.width,
          canvas.height
        );

        ctx.drawImage(
          img,
          0,
          0,
          targetWidth,
          targetHeight
        );

        const compressedBase64 =
          canvas.toDataURL(
            'image/jpeg',
            quality
          );

        resolve(compressedBase64);
      };

      img.onerror = () => {
        reject(
          new Error('Failed to load product image')
        );
      };

      img.src = base64Str;
    });
  };
  const compressProductImages = async (products) => {
    const imageCache = new Map();

    return Promise.all(
      products.map(async (product) => {
        if (!product.pic) {
          return product;
        }

        let compressedImagePromise =
          imageCache.get(product.pic);

        if (!compressedImagePromise) {
          compressedImagePromise = compressBase64Image(
            product.pic
          ).catch((err) => {
            console.error(
              'Failed to compress image:',
              err
            );

            // If compression fails,
            // use original image
            return product.pic;
          });

          imageCache.set(
            product.pic,
            compressedImagePromise
          );
        }

        const compressedPic =
          await compressedImagePromise;

        return {
          ...product,
          pic: compressedPic
        };
      })
    );
  };
  const headers = ['Model Name', 'Product Drawing', 'Composition & Pack.Unit', 'Needle Type', 'Needle Gauge', 'Needle MM', 'Thread Type', 'Thread USP', 'Thread MM', 'Price', 'Description'];
  const getProductsForPDF = (products, values) => {
    const {
      assProducts,
      noAssProducts
    } = addFilters;

    const selLng = values.expLng;

    // Selected categories
    const selectedCategoryIds =
      Array.isArray(values.expCat) && values.expCat.length > 0
        ? values.expCat
        : null;

    // Regional filter.
    // expDef = default price list, so it does NOT filter product.regions.
    const regionalKeys = [
      'expCont',
      'expCntr',
      'expSt',
      'expCt'
    ];

    const selectedRegionKey = regionalKeys.find(
      key => values[key] !== ''
    );

    const regionCode = selectedRegionKey
      ? values[selectedRegionKey]
      : '';

    return products.filter(product => {
      // Category
      if (
        selectedCategoryIds &&
        !selectedCategoryIds.includes(product.category?._id)
      ) {
        return false;
      }

      // Hidden
      if (product.hidden) {
        return false;
      }

      // Language
      if (
        !product.languages?.[selLng] ||
        product.languages[selLng].display !== true
      ) {
        return false;
      }

      // Region
      if (
        regionCode &&
        !product.regions?.[regionCode]
      ) {
        return false;
      }

      // Assortment only
      if (
        assProducts === true &&
        !product.ass_product
      ) {
        return false;
      }

      // Exclude assortment
      if (
        noAssProducts === true &&
        product.ass_product
      ) {
        return false;
      }

      return true;
    });
  };
  // const [triggerGetFont, { isError }] = pricesApi.useLazyGetFontQuery();

  /**
   * Handles the export of product data to a PDF file.
   *
   * This function fetches a font from an API, compresses all product images asynchronously,
   * and then generates a PDF using the provided values and the fetched font. If the font is
   * missing from the API response, the function logs a warning and aborts the export process.
   * Errors during image compression or font fetching are logged to the console.
   *
   * @async
   * @function handleExportPDFSubmit
   * @param {Event} e - The event object triggered by the export action.
   * @param {Object} values - The values used for PDF generation (e.g., user selections or form data).
   * @returns {Promise<void>} Resolves when the PDF export process is complete.
   */
  const handleExportPDFSubmit = async (e, values) => {
    setIsExporting(true);

    try {
      // 1. Filter products that will actually appear in PDF
      const productsForPDF = getProductsForPDF(
        products,
        values
      );

      if (productsForPDF.length === 0) {
        showToast(
          'No products found for selected filters',
          'warning'
        );

        return;
      }

      // 2. Compress images with cache
      const compressedProducts =
        await compressProductImages(productsForPDF);

      // 3. Generate PDF
      await exportPDF(
        compressedProducts,
        values
      );
      // Count successful PDF download
      await trackPdfDownload({
        type: 'price-list',

        language: values.expLng || null,

        priceList: values.expDef || null,

        categories: Array.isArray(values.expCat)
          ? values.expCat
          : [],

        includeWholesale:
          addFilters.includeWAmount,

        includeAdditionalWholesale:
          addFilters.includeWAmountAdd,

        continent: values.expCont || null,
        country: values.expCntr || null,
        state: values.expSt || null,
        city: values.expCt || null
      }).unwrap();
    } catch (err) {
      console.error(
        'Error exporting PDF:',
        err
      );
    } finally {
      setIsExporting(false);
    }
  };
  /**
   * Generates and exports a PDF file containing a formatted price list of products,
   * with support for multiple languages, custom fonts, category and subcategory headers,
   * region-based filtering, and dynamic table styling. The PDF includes images,
   * best seller icons, and handles row spanning for product titles.
   * The file name and content are customized based on selected filters and language.
   *
   * @param {Array<Object>} products - Array of product objects to be included in the PDF.
   * @param {Object} expValues - Export filter values, including selected language, categories, and region.
   * @param {Object} font - Font object containing fontName and font data for embedding in the PDF.
   *
   * @returns {void} Saves the generated PDF file to the user's device.
   */
  const exportPDF = async (products, expValues) => {
    const { includeWAmount, includeWAmountAdd, assProducts, noAssProducts } = addFilters;
    // Get selected language
    const selLng = expValues['expLng'];

    const doc = new jsPDF();

    // Keep original jsPDF addPage
    const originalAddPage =
      doc.addPage.bind(doc);

    // Every new PDF page automatically gets background
    doc.addPage = (...args) => {
      const result = originalAddPage(...args);

      drawPageGradientBackground(doc);

      return result;
    };

    // Background for the very first page
    drawPageGradientBackground(doc);
    // drawPageGradientBackground(doc);
    await loadPdfFont(
      doc,
      geologicaRegularFont,
      'Geologica-Regular.ttf',
      'GeologicaRegular'
    );

    await loadPdfFont(
      doc,
      geologicaMediumFont,
      'Geologica-Medium.ttf',
      'GeologicaMedium'
    );

    await loadPdfFont(
      doc,
      manropeLightFont,
      'Manrope-Light.ttf',
      'ManropeLight'
    );

    await loadPdfFont(
      doc,
      manropeMediumFont,
      'Manrope-Medium.ttf',
      'ManropeMedium'
    );

    await loadPdfFont(
      doc,
      manropeExtraBoldFont,
      'Manrope-ExtraBold.ttf',
      'ManropeExtraBold'
    );
    if (selLng === 'cn') {
      await loadPdfFont(
        doc,
        notoSansSCRegularFont,
        'NotoSansSC-Regular.ttf',
        'NotoSansSC'
      );
    }

    if (selLng === 'ja') {
      await loadPdfFont(
        doc,
        notoSansJPRegularFont,
        'NotoSansJP-Regular.ttf',
        'NotoSansJP'
      );
    }

    const languageFont = selLng === 'cn' ? 'NotoSansSC' : selLng === 'ja' ? 'NotoSansJP' : null;

    doc.setFont('ManropeLight', 'normal');
if (languageFont) {
  doc.setFont(
    languageFont,
    'normal'
  );

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(14);

  doc.text(
    selLng === 'cn'
      ? '中文字体测试'
      : '日本語フォントテスト',
    10,
    50
  );
}
    doc.setTextColor(255, 255, 255);

    let suffixMap = {};
    languages.forEach((lang) => {
      suffixMap[lang.lng_code] = lang.lng_code === 'eng' ? '' : lang.lng_code.charAt(0).toUpperCase() + lang.lng_code.slice(1);
    });
    // let catFrstHdr =
    //   expValues['expCat'] && expValues['expCat'].length > 0
    //     ? products.find((product) => product.category._id === expValues['expCat'][0])?.category.catNameLng?.['catName' + suffixMap[selLng]] ||
    //       products[0].category.catNameLng?.['catName' + suffixMap[selLng]]
    //     : products[0].category.catNameLng?.['catName' + suffixMap[selLng]];
    // drawCategoryBlock(doc, catFrstHdr);
    // Create name for pdf file
    let fileName = '';
    const keysToCheck = ['expDef', 'expCont', 'expCntr', 'expSt', 'expCt'];
    const selFilter = keysToCheck.filter((key) => expValues[key] !== '').map((key) => ({ key, value: expValues[key] }));
    const selRegion = countries.find((key) => selFilter[0]?.key !== 'expDef' && key.cntry_code === selFilter[0]?.value);
    const regionSuffix = selRegion ? `_${selRegion.cntry_name}` : '';
    const suffix = selLng === 'ru' ? `${includeWAmount ? ' ОР' : ' Р'}${regionSuffix}` : `${includeWAmount ? ' RW' : ' R'}${expValues['expDef'] === 'eur' ? ' USD_Europe' : ' USD'}${regionSuffix}`;
    fileName = `${selLng.toUpperCase()} Koru Products${suffix}`;

    let rowsPerPage = 1;
    let checkIndx = null;
    // Check selected categories
    const selCategories = expValues['expCat'].length > 0 ? categories.filter((cat) => expValues['expCat'].includes(cat._id)) : categories;

    // Assign products to each category
    selCategories.forEach((category, index) => {
      let headerData = [];
      let bodyData = [];
      var rowSpanTitle = {};
      const regionCode = selRegion?.cntry_code || '';

      const catProducts = products.filter(
        (product) =>
          product.category._id === category._id &&
          !product.hidden &&
          product.languages[selLng] &&
          product.languages[selLng].display === true &&
          (!regionCode || product.regions[regionCode]) &&
          (assProducts === false || product.ass_product) &&
          (noAssProducts === false || !product.ass_product)
      );

      let catHdr = category.catNameLng?.['catName' + suffixMap[selLng]];
      catProducts.sort((a, b) => a.sortNo - b.sortNo);
      // Create header
      headerData.push(createHeader(catProducts, headers, expValues, includeWAmount, includeWAmountAdd));
      const columnRoles = createColumnRoles(catProducts, expValues, includeWAmount, includeWAmountAdd);
      if (catProducts.length > 0) {
        let indx = -1;
        const hasNonEmptyWAmountAdd = catProducts.some((product) => product.w_amount_add !== '');
        const addedSubCatHeaders = new Set();
        catProducts.forEach(function (product) {
          // const currRAmount = product.r_amount || '';
          // prevRAmount = currRAmount;
          // Add subcategory header if not already added
          const subCatId = product.subCategory?._id;

          if (subCatId && !addedSubCatHeaders.has(subCatId)) {
            const subCatName = product.subCategory?.[`subCatName${selLng.charAt(0).toUpperCase() + selLng.slice(1)}`] || product.subCategory?.subCatName || 'Unknown Subcategory';
            bodyData.push([
              {
                content: subCatName,
                colSpan: getTotalCols(headerData[0]),
                styles: { halign: 'center', fillColor: [0, 176, 240] } // Customize as needed
              }
            ]);
            addedSubCatHeaders.add(subCatId);
          }
          let rowData = [];
          indx++;
          rowData = createArrayData(product, category?._id, expValues, includeWAmount, includeWAmountAdd, hasNonEmptyWAmountAdd);

          let selTitle = product.languages[selLng]?.title || product.languages['eng']?.title;
          if (!rowSpanTitle.hasOwnProperty(selTitle)) {
            rowSpanTitle[selTitle] = { rowspan: 1, index: indx, content: selTitle }; // Initialize rowspan count, index, and content if not exists
          } else {
            rowSpanTitle[selTitle].rowspan++; // Increment rowspan count if exists
          }
          bodyData.push(rowData);
        });
        // Check if the current row exceeds the rows per page limit
        if (index > 0 && index % rowsPerPage === 0) {
          if (checkIndx !== null) {
            doc.addPage();
          }
        }

        checkIndx = index;

        drawCategoryBlock(doc, catHdr, languageFont, selLng);
        // Set style for each column in the table
        const columnStyles = createPdfColumnStyles(
          category,
          catProducts,
          expValues,
          includeWAmount,
          includeWAmountAdd
        );
        const headerMatches = (header, prefixes) => prefixes.some((prefix) => header.startsWith(prefix));

        // Create new body data
        const newBodyData = [];
        // Row span titles
        const usedTitles = new Set();
        // Row span r prices in the file
        // const rowSpanMap = new Map(); // key: row index, value: { r_price, rowSpan }
        // const seenRPrices = new Set();
        // Step 1: Identify consecutive r_price sequences
        // let lastPrice = null;
        // let startIndex = 0;
        // let count = 0;
        // Step 3: Row span titles, images, r prices, w prices
        bodyData.forEach((row, rowIndex) => {
          const firstCell = row[0];
          const newRow = [...row];
          // const rPriceCellIndex = newRow.findIndex((cell) => typeof cell === 'object' && cell !== null && 'r_price' in cell);
          // const rowSpan = rowSpanMap.has(rPriceCellIndex) ? rowSpanMap.get(rPriceCellIndex).rowSpan : 1;
          // row span titles and images
          if (typeof firstCell === 'object' && firstCell.content) {
            const title = firstCell.content;
            if (usedTitles.has(title)) {
              // Already seen: remove the first column
              newRow.splice(0, 2);
            } else {
              // First occurrence: add rowspan
              const rowspanInfo = rowSpanTitle[title];
              if (rowspanInfo) {
                newRow[0] = {
                  ...firstCell,
                  rowSpan: rowspanInfo.rowspan,
                  styles: {
                    valign: 'middle',
                    halign: 'center',
                    minCellHeight: 20
                  }
                };
                newRow[1] = {
                  content: '',
                  rowSpan: rowspanInfo.rowspan
                };
              }
              usedTitles.add(title);
            }
          }
          newBodyData.push(newRow);
        });

        // const pageHeight = doc.internal.pageSize.height;
        // const marginTop = 55;
        // const marginBottom = 10;
        // const availablePageHeight = pageHeight - marginTop - marginBottom;

        // const estimatedRowHeight = 20; // based on your `minCellHeight`
        // const maxRowsPerPage = Math.floor(availablePageHeight / estimatedRowHeight);

        const adjustedBodyData = newBodyData;
        // for (let i = 0; i < newBodyData.length; ) {
        //   const row = newBodyData[i];

        //   // Detect a starting row with rowspan (has 2 objects at the beginning)
        //   if (typeof row[0] === 'object' && row[0]?.rowSpan > 1) {
        //     const rowSpan = row[0].rowSpan;
        //     const title = row[0].content;

        //     if (rowSpan > maxRowsPerPage) {
        //       let remaining = rowSpan;
        //       let chunkIndex = 0;
        //       while (remaining > 0) {
        //         const spanThisPage = Math.min(remaining, maxRowsPerPage);
        //         const slice = newBodyData.slice(i + chunkIndex * maxRowsPerPage, i + chunkIndex * maxRowsPerPage + spanThisPage);

        //         // Modify only the first row to include the header
        //         if (chunkIndex === 0) {
        //           slice[0][0].rowSpan = spanThisPage;
        //           slice[0][1].rowSpan = spanThisPage;
        //         } else {
        //           // Clone header cell for next page continuation
        //           slice[0].unshift(
        //             {
        //               content: title,
        //               styles: row[0].styles,
        //               rowSpan: spanThisPage
        //             },
        //             {
        //               content: '',
        //               rowSpan: spanThisPage
        //             }
        //           );
        //         }

        //         adjustedBodyData.push(...slice);
        //         remaining -= spanThisPage;
        //         chunkIndex++;
        //       }
        //       i += rowSpan;
        //     } else {
        //       adjustedBodyData.push(...newBodyData.slice(i, i + rowSpan));
        //       i += rowSpan;
        //     }
        //   } else {
        //     // No rowspan, just push row
        //     adjustedBodyData.push(row);
        //     i++;
        //   }
        // }
// ==========================================
// DETECT PAGE FOR EACH ROW BEFORE PRICE MERGE
// ==========================================

        const rowPageMap = new Map();

        const pagesBeforeProbe =
          doc.internal.getNumberOfPages();

        const originalPage =
          doc.internal.getCurrentPageInfo().pageNumber;

        // Temporary page only for calculating layout
        doc.addPage();

        const probeStartPage =
          doc.internal.getCurrentPageInfo().pageNumber;

        doc.setLineHeightFactor(
          TABLE_LINE_HEIGHT_FACTOR
        );

        doc.autoTable({
          head: [headerData[0]],
          body: adjustedBodyData,
          columnStyles: columnStyles,

          startY: FIRST_PAGE_TABLE_START_Y,

          margin: {
            horizontal: TABLE_MARGIN_X
          },

          theme: 'grid',
          pageBreak: 'auto',
          rowPageBreak: 'avoid',

          styles: {
            font: languageFont || 'ManropeLight',
            fontSize: COMPOSITION_FONT_SIZE,
            valign: 'middle',
            halign: 'center',
            lineWidth: TABLE_INNER_LINE_WIDTH
          },

          headStyles: {
            font: languageFont || 'GeologicaRegular',
            fontSize: TABLE_HEADER_FONT_SIZE
          },

          showHead: 'everyPage',

          willDrawCell: data => {
            if (
              data.section === 'body' &&
              !rowPageMap.has(data.row.index)
            ) {
              const currentPage =
                doc.internal
                  .getCurrentPageInfo()
                  .pageNumber;

              rowPageMap.set(
                data.row.index,
                currentPage - probeStartPage + 1
              );
            }
          }
        });

        // Delete all temporary probe pages
        while (doc.internal.getNumberOfPages() > pagesBeforeProbe) {
          doc.deletePage( doc.internal.getNumberOfPages() );
        }

        // Return to real category page
        doc.setPage(originalPage);

        // ==========================================
        // NOW MERGE PRICES ONLY INSIDE EACH PAGE
        // ==========================================

        mergeRepeatedPriceRows(
          adjustedBodyData,
          columnRoles,
          rowPageMap
        );
        const tableStartPage = doc.internal.getCurrentPageInfo().pageNumber;

        const tableStartY = FIRST_PAGE_TABLE_START_Y;
        // Typography line-height
        doc.setLineHeightFactor(TABLE_LINE_HEIGHT_FACTOR);
        doc.autoTable({
          head: [headerData[0]], // don't forget square brackets, columns is an array of string
          headStyles: { font: languageFont || 'GeologicaRegular', fillColor: '#539FD3', textColor: '#FFFFFF', fontSize: TABLE_HEADER_FONT_SIZE },
          body: adjustedBodyData,
          columnStyles: columnStyles,
          startY: tableStartY,
          margin: { horizontal: TABLE_MARGIN_X },
          theme: 'grid',
          pageBreak: 'auto',
          rowPageBreak: 'avoid',
          styles: {
            font: languageFont || 'ManropeLight', // Replace with your desired font name
            fontSize: COMPOSITION_FONT_SIZE, // Replace with your desired font size
            textColor: [0, 0, 0],
            valign: 'middle', // Center vertically
            halign: 'center', // Center horizontally
            lineColor: TABLE_BORDER_COLOR,
            lineWidth: TABLE_INNER_LINE_WIDTH,
          },
          showHead: 'everyPage',
          didDrawPage: (data) => {
            const currentDocPage =
              doc.internal.getCurrentPageInfo().pageNumber;

            const topY =
              currentDocPage === tableStartPage
                ? tableStartY
                : data.settings.margin.top;

            const bottomY = data.cursor.y;

            // Rounded top
            drawTableTopRoundedCorners(
              doc,
              topY,
              TABLE_MARGIN_X,
              TABLE_RADIUS
            );

            // Rounded bottom
            drawTableBottomRoundedCorners(
              doc,
              bottomY,
              TABLE_MARGIN_X,
              TABLE_RADIUS
            );

            // Thick outer left/right borders
            const pageWidth =
              doc.internal.pageSize.getWidth();

            const leftX = TABLE_MARGIN_X;
            const rightX =
              pageWidth - TABLE_MARGIN_X;

            doc.setDrawColor(...TABLE_BORDER_COLOR);
            doc.setLineWidth(TABLE_LINE_WIDTH);

            // Left outer border
            doc.line(
              leftX,
              topY + TABLE_RADIUS,
              leftX,
              bottomY - TABLE_RADIUS
            );

            // Right outer border
            doc.line(
              rightX,
              topY + TABLE_RADIUS,
              rightX,
              bottomY - TABLE_RADIUS
            );
          },
          didParseCell: (data) => {
            if (data.section === 'head') {
              const lastColumnIndex = columnRoles.length - 1;

              // First header cell — move text away from left rounded corner
              if (data.column.index === 0) {
                data.cell.styles.cellPadding = {
                  top: 2,
                  right: 1,
                  bottom: 2,
                  left: TABLE_RADIUS + 2
                };
              }

              // Last header cell — move text away from right rounded corner
              if (data.column.index === lastColumnIndex) {
                data.cell.styles.cellPadding = {
                  top: 2,
                  right: TABLE_RADIUS + 2,
                  bottom: 2,
                  left: 1
                };
              }
            }
                      // Product name
            if (data.section === 'body' && data.row.raw.length > 1) {
              const role = columnRoles[data.column.index];

          switch (role) {
            case 'productName':
              data.cell.styles.font = languageFont || 'ManropeExtraBold';
              data.cell.styles.fontSize = PRODUCT_NAME_FONT_SIZE;
              break;

            case 'composition':
              data.cell.styles.font = languageFont || 'ManropeLight';
              data.cell.styles.fontSize = COMPOSITION_FONT_SIZE;
              break;

            case 'description':
              data.cell.styles.font = languageFont || 'ManropeLight';
              data.cell.styles.fontSize = DESCRIPTION_FONT_SIZE;
              break;

            case 'price':
              // Price contains only numbers/currency,
              // so keep ExtraBold even in CN/JP
              data.cell.styles.font = 'ManropeExtraBold';
              data.cell.styles.fontSize = PRICE_FONT_SIZE;
              break;

            case 'threadSpec':
              data.cell.styles.font = languageFont || 'ManropeMedium';
              data.cell.styles.fontSize = THREAD_SPEC_FONT_SIZE;
              break;

            default:
              break;
          }
            }
                      // Apply text color to the first column
            if (data.column.index === 0 && data.section === 'body' && data.row.raw.length > 1) {
              data.cell.styles.textColor = '#000000';
            }
            if (
              (data.column.index === 2 ||
                data.column.index === 3 ||
                data.column.index === 4 ||
                data.column.index === 5 ||
                data.column.index === 6 ||
                data.column.index === 7 ||
                data.column.index === 8 ||
                data.column.index === 9) &&
              data.section === 'body'
            ) {
              data.cell.styles.textColor = '#000000';
              // data.cell.styles.fontWeight = '100';
            }
            if (data.row.section === 'body' && columnHeaders[0].includes(data.row.raw[0])) {
              data.cell.styles.textColor = [250, 250, 250];
              data.cell.styles.fontSize = 8;
              data.cell.styles.fillColor = '#539FD3';
            }
            if (data.row.section === 'body' && data.row.raw.length === 1) {
              data.cell.styles.textColor = [250, 250, 250];
              data.cell.styles.fontSize = 8;
              data.cell.styles.fillColor = '#539FD3';
              data.cell.colSpan = headerData[0].length;
            }
          },
          didDrawCell: function (data) {
            if (data.column.index === 0 && data.cell.section === 'body') {
              if (data.row.index !== -1 && newBodyData[data.row.index][newBodyData[data.row.index].length - 5] === true) {
                doc.addImage(best_seller_img, 'JPEG', data.cell.x + 2, data.cell.y + 2, 6, 6);
              }
            }
            if (data.column.index === 1 && data.cell.section === 'body') {
              if (data.row.index !== -1) {
                const imageData = newBodyData[data.row.index][newBodyData[data.row.index].length - 1];
                if (imageData) {
                  var imageWidth =
                    data.row.raw[data.row.raw.length - 4] === '646dad3156ed436b64227e32' ||
                    data.row.raw[data.row.raw.length - 4] === '647004a0b4fb05147748ca66' ||
                    data.row.raw[data.row.raw.length - 4] === '647004a8b4fb05147748ca67'
                      ? 26
                      : 18; // Width of your image
                  var imageHeight =
                    data.row.raw[data.row.raw.length - 4] === '646dad3156ed436b64227e32' ||
                    data.row.raw[data.row.raw.length - 4] === '647004a0b4fb05147748ca66' ||
                    data.row.raw[data.row.raw.length - 4] === '647004a8b4fb05147748ca67'
                      ? 26
                      : 18; // Height of your image

                  // Calculate the horizontal and vertical centering offsets
                  var cellX = data.cell.x;
                  var cellY = data.cell.y;
                  var cellWidth = data.cell.width;
                  var cellHeight = data.cell.height;

                  var xOffset = (cellWidth - imageWidth) / 2;
                  var yOffset = (cellHeight - imageHeight) / 2;
                  doc.addImage(imageData, 'JPEG', cellX + xOffset, cellY + yOffset, imageWidth, imageHeight);
                }
              }
            }
            // if (data.row.index === 0 && data.row.raw[5].rowSpan > 1) {
            // }
          }
        });

      }
    });
    doc.save(`${fileName}.pdf`);
  };
  const [rowToDelete, setRowToDelete] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  /**
   * Handles the event when a product is selected for deletion.
   * Sets the selected product, stores the product's ID for deletion,
   * and displays the delete confirmation alert.
   *
   * @param {Object} product - The product object to be deleted.
   */
  const handleProductClickDelete = (product) => {
    setSelectedProduct(product);
    setRowToDelete(product._id);
    setShowDeleteAlert(true);
  };
  /**
   * Handles the deletion of a product from the price list.
   * Closes the delete alert, attempts to delete the selected product,
   * resets relevant state variables, and displays a toast notification
   * indicating success or failure.
   *
   * @async
   * @function
   * @returns {Promise<void>} Resolves when the deletion process is complete.
   */
  const handleDeleteProduct = async () => {
    setShowDeleteAlert(false);
    try {
      await deleteProduct(rowToDelete);
      setRowToDelete(null);
      setSelectedProduct([]);
      showToast('Product deleted successfully');
    } catch (error) {
      console.error('Failed to delete row:', error);
      showToast('Failed to delete row', 'error');
    }
  };
  /**
   * Renders the modal content for creating or editing a product in a multi-step wizard interface.
   *
   * The modal includes three main steps:
   * 1. Default Product Creation: Allows input of general product information, category selection, image upload, and price details.
   * 2. Language Information: Enables entry of product details in multiple languages, excluding English.
   * 3. Region Info Prices: Provides fields to set region-specific pricing for continents, countries, states, and cities.
   *
   * The form supports validation, file uploads, and navigation between steps. It dynamically generates fields based on available categories, subcategories, languages, and countries.
   *
   * @type {JSX.Element}
   * @param {Object} selProduct - The selected product object containing all product details.
   * @param {number} activeTab - The current active step in the wizard.
   * @param {Function} toggleTab - Function to switch between wizard steps.
   * @param {Array} passedSteps - Array of completed step numbers.
   * @param {Array} categories - List of available product categories.
   * @param {Array} sub_categories - List of available subcategories.
   * @param {Array} languages - List of supported languages.
   * @param {Array} countries - List of available countries, states, and cities for region pricing.
   * @param {boolean} isEdit - Flag indicating if the modal is in edit mode.
   * @param {number} latestSortNo - The latest sort number for product ordering.
   * @param {string} lng - Current language code.
   * @param {Array} selectedFiles - List of files selected for upload.
   * @param {Function} handleAcceptedFiles - Callback for handling file uploads.
   * @param {Function} handleCategoryChange - Callback for handling category selection changes.
   * @param {Function} submitFirstStep - Callback for submitting the first step.
   * @param {Function} submitSecondStep - Callback for submitting the second step.
   * @param {Function} handleNewProductSubmit - Callback for submitting the final product creation or edit.
   * @param {Object} modalBodyRef - Ref for the modal body element.
   */
  const modalNewProductContent = (
    <div ref={modalBodyRef} style={{ overflowY: 'auto', maxHeight: '85vh' }} className='wizard clearfix'>
      <div className='steps clearfix'>
        <ul>
          <NavItem className={classnames({ current: activeTab === 1 })}>
            <NavLink
              className={classnames({ active: activeTab === 1 })}
              onClick={() => {
                toggleTab(1);
              }}
            >
              <span className='number'>1.</span> Create Default Product
            </NavLink>
          </NavItem>
          <NavItem className={classnames({ current: activeTab === 2 })}>
            <NavLink
              disabled={!(passedSteps || []).includes(2)}
              className={classnames({ active: activeTab === 2 })}
              onClick={() => {
                toggleTab(2);
              }}
            >
              <span className='number'>2.</span> <span>Add Language Info</span>
            </NavLink>
          </NavItem>
          <NavItem className={classnames({ current: activeTab === 3 })}>
            <NavLink
              disabled={!(passedSteps || []).includes(3)}
              className={classnames({ active: activeTab === 3 })}
              onClick={() => {
                toggleTab(3);
              }}
            >
              <span className='number'>3.</span> <span>Add Region Info Prices</span>
            </NavLink>
          </NavItem>
        </ul>
      </div>
      <div className='content clearfix'>
        <TabContent activeTab={activeTab} className='body'>
          <TabPane key={1} tabId={1}>
            <AvForm onValidSubmit={submitFirstStep}>
              <Row>
                <Col md='12' className='justify-content-center d-flex mt-2'>
                  <b>General Information</b>
                  <FormGroup className='ms-3 font-size-18 d-flex'>
                    <Label htmlFor='ass_product' className='form-check-label '>
                      Assortment Product
                    </Label>
                    <AvField name='ass_product' type='checkbox' className='mt-2 form-check-input' id='ass_product' value={selProduct.ass_product || ''} />
                  </FormGroup>
                </Col>
              </Row>
              <hr />
              <Row>
                <Col md={1}>
                  <FormGroup className='mb-3'>
                    <AvField
                      name='sortNo'
                      id='sortNo'
                      label='Sort No'
                      type='number'
                      errorMessage='Invalid No'
                      autoComplete='off'
                      validate={{
                        required: {
                          value: true
                        }
                      }}
                      disabled={latestSortNo === 1 && !isEdit}
                      value={selProduct.sortNo || latestSortNo}
                    />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup className='mb-3'>
                    <AvField
                      name='nomenclatureCode'
                      id='nomenclatureCode'
                      label='Nomenclatur Code'
                      type='text'
                      errorMessage='Invalid No'
                      autoComplete='off'
                      validate={{
                        required: {
                          value: true
                        }
                      }}
                      value={selProduct.nomenclatureCode}
                    />
                  </FormGroup>
                </Col>
                <Col md={1}>
                  <FormGroup className='mb-3'>
                    <AvField
                      type='select'
                      name='category'
                      className='form-select'
                      label='Category'
                      validate={{
                        required: {
                          value: true
                        }
                      }}
                      onChange={handleCategoryChange}
                      value={selProduct.category && selProduct.category._id ? selProduct.category._id : selProduct.category ? selProduct.category : ''}
                    >
                      <option value=''>Category</option>
                      {categories &&
                        categories.map((category, index) => {
                          const capitalizedLng = lng.charAt(0).toUpperCase() + lng.slice(1);
                          const key = `catName${capitalizedLng}`;
                          const catName = key === 'catNameEng' ? category.catNameLng?.catName : category.catNameLng?.[key];
                          return (
                            <option value={category._id} key={index}>
                              {catName}
                            </option>
                          );
                        })}
                    </AvField>
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup className='mb-3'>
                    <AvField
                      type='select'
                      name='subCategory'
                      className='form-select'
                      label='Sub Category'
                      value={selProduct.subCategory && selProduct.subCategory._id ? selProduct.subCategory._id : selProduct.subCategory ? selProduct.subCategory : ''}
                    >
                      <option value=''>Sub Category</option>
                      {sub_categories &&
                        sub_categories.map((subCategory, index) => {
                          return (
                            <option value={subCategory._id} key={index}>
                              {subCategory.subCatName}
                            </option>
                          );
                        })}
                    </AvField>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup className='mb-3'>
                    <AvField
                      name='title'
                      id='title'
                      label='Title'
                      type='text'
                      errorMessage='Invalid Title'
                      autoComplete='off'
                      validate={{
                        required: {
                          value: true
                        }
                      }}
                      value={(selProduct.languages && selProduct.languages.eng.title) || ''}
                    />
                  </FormGroup>
                  <FormGroup className='mb-3 new-product'>
                    <Label htmlFor='customCheckcolor1' className='form-check-label new-product-label'>
                      <i className='mdi mdi-new-box mdi-24px' />
                    </Label>
                    <AvField name='new_product' type='checkbox' className='form-check-input new-product-input ' id='customCheckcolor1' value={selProduct.new_product || ''} />
                  </FormGroup>
                  <FormGroup className='mb-3 best-seller'>
                    <Label htmlFor='customCheckcolor2' className='form-check-label best-seller-label'>
                      <i className='mdi mdi-bookmark-check mdi-24px' />
                    </Label>
                    <AvField name='best_seller' type='checkbox' className='form-check-input best-seller-input ' id='customCheckcolor2' value={selProduct.best_seller || ''} />
                  </FormGroup>
                </Col>
                <Col md={1}>
                  <Dropzone accept='.jpeg,.jpg,.png' onDrop={(acceptedFiles) => handleAcceptedFiles(acceptedFiles)}>
                    {({ getRootProps, getInputProps }) => (
                      <div className='dropzone_price'>
                        <div className='dz-message-price needsclick' {...getRootProps()}>
                          <input {...getInputProps()} />
                          <div className='mb-3'>
                            <i className='display-4 text-muted bx bxs-cloud-upload' />
                          </div>
                        </div>
                      </div>
                    )}
                  </Dropzone>
                </Col>
                <Col lg={2}>
                  <div className='dropzone-previews mt-3' id='file-previews'>
                    {selectedFiles.map((f, i) => {
                      return (
                        <Card className='mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete' key={i + '-file'}>
                          <div className='p-2'>
                            <Row className='align-items-center'>
                              <Col className='col-auto m-auto'>
                                <div className='new-photo'>
                                  <img
                                    data-dz-thumbnail=''
                                    height='80'
                                    className='bg-light'
                                    // alt={f !== null ? f.name : ""}
                                    src={f === null ? '' : f.preview === undefined ? selProduct.pic : f.preview}
                                  />
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </Col>
              </Row>
              <hr />
              <div className='text-center font-size-18 mt-1 mb-5'>
                <b>Price Information</b>
                <hr />
              </div>
              <Row>
                <Col md={2}></Col>
                <Col md={3}>
                  <FormGroup className='mb-3'>
                    <AvField
                      name='rusd'
                      label='R USD Price'
                      type='text'
                      errorMessage='Invalid Price'
                      autoComplete='off'
                      validate={{
                        required: {
                          value: true
                        }
                      }}
                      value={selProduct.prices ? selProduct.prices.usd.price_r : ''}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup className='mb-3'>
                    <AvField
                      name='reur'
                      label='R USD Europe Price'
                      type='text'
                      errorMessage='Invalid Price'
                      autoComplete='off'
                      validate={{
                        required: {
                          value: true
                        }
                      }}
                      value={selProduct.prices ? selProduct.prices.eur.price_r : ''}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup className='mb-3'>
                    <AvField
                      name='r_amount'
                      label='R Amount'
                      placeholder='from 1 to 99'
                      type='text'
                      errorMessage='Invalid'
                      autoComplete='off'
                      validate={{
                        required: {
                          value: true
                        }
                      }}
                      value={selProduct.r_amount || ''}
                    />
                  </FormGroup>
                </Col>
                <Col md={1}></Col>
              </Row>
              <Row>
                <Col md={2}></Col>
                <Col md={3}>
                  <FormGroup className='mb-3'>
                    <AvField
                      name='wusd'
                      label='W USD Price'
                      type='text'
                      errorMessage='Invalid Price'
                      autoComplete='off'
                      validate={{
                        required: {
                          value: true
                        }
                      }}
                      value={selProduct.prices ? selProduct.prices.usd.price_w : ''}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup className='mb-3'>
                    <AvField
                      name='weur'
                      label='W USD Europe Price'
                      type='text'
                      errorMessage='Invalid Price'
                      autoComplete='off'
                      validate={{
                        required: {
                          value: true
                        }
                      }}
                      value={selProduct.prices ? selProduct.prices.eur.price_w : ''}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup className='mb-3'>
                    <AvField
                      name='w_amount'
                      label='W Amount'
                      type='text'
                      placeholder='from 100'
                      errorMessage='Invalid'
                      autoComplete='off'
                      validate={{
                        required: {
                          value: true
                        }
                      }}
                      value={selProduct.w_amount || ''}
                    />
                  </FormGroup>
                </Col>
                <Col md={1}></Col>
              </Row>
              <Row>
                <Col md={2}></Col>
                <Col md={3}>
                  <FormGroup className='mb-3'>
                    <AvField
                      name='wusd_add'
                      label='Additional W USD Price'
                      type='text'
                      errorMessage='Invalid Price'
                      autoComplete='off'
                      value={selProduct.prices ? selProduct.prices.usd.price_w_add : ''}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup className='mb-3'>
                    <AvField
                      name='weur_add'
                      label='Additional W USD Europe Price'
                      type='text'
                      errorMessage='Invalid Price'
                      autoComplete='off'
                      value={selProduct.prices ? selProduct.prices.eur.price_w_add : ''}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup className='mb-3'>
                    <AvField name='w_amount_add' label='Additional W Amount' type='text' placeholder='from 300' errorMessage='Invalid' autoComplete='off' value={selProduct.w_amount_add || ''} />
                  </FormGroup>
                </Col>
                <Col md={1}></Col>
              </Row>
              <hr />
              <div className='text-center font-size-18 mt-1 mb-5'>
                <b>Additional Information</b>
                <hr />
              </div>
              <Row>
                <Col md={6}>
                  <FormGroup className='mb-3'>
                    <AvField
                      name='composition'
                      label='Composition'
                      type='textarea'
                      autoComplete='off'
                      className='font-size-16'
                      rows='5'
                      value={selProduct.languages ? selProduct.languages.eng.composition : ''}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup className='mb-3'>
                    <AvField
                      name='description'
                      label='Description'
                      type='textarea'
                      autoComplete='off'
                      className='font-size-16'
                      rows='5'
                      value={selProduct.languages ? selProduct.languages.eng.description : ''}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={2}>
                  <FormGroup className='mb-3'>
                    <AvField name='needle_type' label='Needle Type' type='text' autoComplete='off' value={selProduct.languages ? selProduct.languages.eng.needle_type : ''} />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup className='mb-3'>
                    <AvField name='needle_gage' label='Needle Gauge' type='text' autoComplete='off' value={selProduct.languages ? selProduct.languages.eng.needle_gage : ''} />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup className='mb-3'>
                    <AvField name='needle_mm' label='Needle MM' type='text' autoComplete='off' value={selProduct.languages ? selProduct.languages.eng.needle_mm : ''} />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup className='mb-3'>
                    <AvField name='thread_type' label='Thread Type' type='text' autoComplete='off' value={selProduct.languages ? selProduct.languages.eng.thread_type : ''} />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup className='mb-3'>
                    <AvField name='thread_gage' label='Thread USP' type='text' autoComplete='off' value={selProduct.languages ? selProduct.languages.eng.thread_gage : ''} />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup className='mb-3'>
                    <AvField name='thread_mm' label='Thread MM' type='text' autoComplete='off' value={selProduct.languages ? selProduct.languages.eng.thread_mm : ''} />
                  </FormGroup>
                </Col>
              </Row>
              <Row className='prevNext'>
                <Col>
                  <div className='text-end'>
                    <button className='btn btn-primary disabled me-3'> Previous</button>
                    <button className='btn btn-primary'>Next</button>
                  </div>
                </Col>
              </Row>
            </AvForm>
          </TabPane>
          <TabPane key={2} tabId={2}>
            <AvForm onValidSubmit={submitSecondStep}>
              {languages
                .filter((lng) => lng.lng_code !== 'eng')
                .map((lng, index) => (
                  <>
                    <div className='text-center font-size-18 mt-1 mb-2'>
                      <hr />
                      <b>{lng.lng_name}</b>
                      <hr />
                    </div>
                    <Row>
                      <Col md={6}>
                        <FormGroup className='mb-3'>
                          <AvField
                            name={`title_${lng.lng_code}`}
                            label='Title'
                            type='text'
                            value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].title : selProduct.languages?.eng?.title}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={2}>
                        <FormGroup className='mb-3'>
                          <AvField
                            name={`needle_type_${lng.lng_code}`}
                            label='Needle Type'
                            type='text'
                            autoComplete='off'
                            value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].needle_type : selProduct.languages?.eng?.needle_type}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup className='mb-3'>
                          <AvField
                            name={`needle_gage_${lng.lng_code}`}
                            label='Needle Gage'
                            type='text'
                            autoComplete='off'
                            value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].needle_gage : selProduct.languages?.eng?.needle_gage}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup className='mb-3'>
                          <AvField
                            name={`needle_mm_${lng.lng_code}`}
                            label='Needle MM'
                            type='text'
                            autoComplete='off'
                            value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].needle_mm : selProduct.languages?.eng?.needle_mm}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup className='mb-3'>
                          <AvField
                            name={`thread_type_${lng.lng_code}`}
                            label='Thread Type'
                            type='text'
                            autoComplete='off'
                            value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].thread_type : selProduct.languages?.eng?.thread_type}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup className='mb-3'>
                          <AvField
                            name={`thread_gage_${lng.lng_code}`}
                            label='Thread Gage'
                            type='text'
                            autoComplete='off'
                            value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].thread_gage : selProduct.languages?.eng?.thread_gage}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup className='mb-3'>
                          <AvField
                            name={`thread_mm_${lng.lng_code}`}
                            label='Thread MM'
                            type='text'
                            autoComplete='off'
                            value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].thread_mm : selProduct.languages?.eng?.thread_mm}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup className='mb-3'>
                          <AvField
                            name={`composition_${lng.lng_code}`}
                            label='Composition'
                            type='textarea'
                            autoComplete='off'
                            className='font-size-16'
                            rows='5'
                            value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].composition : ''}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className='mb-3'>
                          <AvField
                            name={`description_${lng.lng_code}`}
                            label='Description'
                            type='textarea'
                            autoComplete='off'
                            className='font-size-16'
                            rows='5'
                            value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].description : ''}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </>
                ))}
              <Row className='prevNext'>
                <Col>
                  <div className='text-end'>
                    <Link
                      to='#'
                      className='btn btn-primary me-3'
                      onClick={() => {
                        toggleTab(activeTab - 1);
                      }}
                    >
                      {' '}
                      Previous
                    </Link>
                    <button className='btn btn-primary'>Next</button>
                  </div>
                </Col>
              </Row>
            </AvForm>
          </TabPane>
          <TabPane key={3} tabId={3}>
            <AvForm onValidSubmit={handleNewProductSubmit}>
              <Row>
                {countries.filter((c) => c.region === 'continent').length > 0 && (
                  <Col md={6}>
                    <div className='text-center font-size-18 mt-1 mb-2'>
                      <hr />
                      <b>Continent</b>
                      <hr />
                    </div>
                    {countries
                      .filter((c) => c.region === 'continent')
                      .map((cont, index) => {
                        return (
                          <Row key={index}>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField name={'name_' + cont.cntry_code} label='Continent' type='text' disabled={true} value={cont.cntry_name} />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField
                                  name={'price_r_' + cont.cntry_code}
                                  label='R Price'
                                  type='text'
                                  autoComplete='off'
                                  value={selProduct.regions && selProduct.regions[`${cont.cntry_code}`] ? selProduct.regions[`${cont.cntry_code}`].price_r : ''}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField
                                  name={'price_w_' + cont.cntry_code}
                                  label='W Price'
                                  type='text'
                                  autoComplete='off'
                                  value={selProduct.regions && selProduct.regions[`${cont.cntry_code}`] ? selProduct.regions[`${cont.cntry_code}`].price_w : ''}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField
                                  name={'price_w_add_' + cont.cntry_code}
                                  label='W Price Add'
                                  type='text'
                                  autoComplete='off'
                                  disabled={selProduct.w_amount_add === ''}
                                  value={selProduct.regions && selProduct.regions[`${cont.cntry_code}`] ? selProduct.regions[`${cont.cntry_code}`].price_w_add : ''}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        );
                      })}
                  </Col>
                )}
                {countries.filter((c) => c.region === 'country').length > 0 && (
                  <Col md={6}>
                    <div className='text-center font-size-18 mt-1 mb-2'>
                      <hr />
                      <b>Country</b>
                      <hr />
                    </div>
                    {countries
                      .filter((c) => c.region === 'country')
                      .map((cntr, index) => {
                        return (
                          <Row key={index}>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField name={'name_' + cntr.cntry_code} label='Country' type='text' disabled={true} value={cntr.cntry_name} />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField
                                  name={'price_r_' + cntr.cntry_code}
                                  label='R Price'
                                  type='text'
                                  autoComplete='off'
                                  value={selProduct.regions && selProduct.regions[`${cntr.cntry_code}`] ? selProduct.regions[`${cntr.cntry_code}`].price_r : ''}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField
                                  name={'price_w_' + cntr.cntry_code}
                                  label='W Price'
                                  type='text'
                                  autoComplete='off'
                                  value={selProduct.regions && selProduct.regions[`${cntr.cntry_code}`] ? selProduct.regions[`${cntr.cntry_code}`].price_w : ''}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField
                                  name={'price_w_add_' + cntr.cntry_code}
                                  label='W Price Add'
                                  type='text'
                                  autoComplete='off'
                                  disabled={selProduct.w_amount_add === ''}
                                  value={selProduct.regions && selProduct.regions[`${cntr.cntry_code}`] ? selProduct.regions[`${cntr.cntry_code}`].price_w_add : ''}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        );
                      })}
                  </Col>
                )}
                {countries.filter((c) => c.region === 'state').length > 0 && (
                  <Col md={6}>
                    <div className='text-center font-size-18 mt-1 mb-2'>
                      <hr />
                      <b>State</b>
                      <hr />
                    </div>
                    {countries
                      .filter((c) => c.region === 'state')
                      .map((st, index) => {
                        return (
                          <Row key={index}>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField name={'name_' + st.cntry_code} label='State' type='text' disabled={true} value={st.cntry_name} />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField
                                  name={'price_r_' + st.cntry_code}
                                  label='R Price'
                                  type='text'
                                  autoComplete='off'
                                  value={selProduct.regions && selProduct.regions[`${st.cntry_code}`] ? selProduct.regions[`${st.cntry_code}`].price_r : ''}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField
                                  name={'price_w_' + st.cntry_code}
                                  label='W Price'
                                  type='text'
                                  autoComplete='off'
                                  value={selProduct.regions && selProduct.regions[`${st.cntry_code}`] ? selProduct.regions[`${st.cntry_code}`].price_w : ''}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField
                                  name={'price_w_add_' + st.cntry_code}
                                  label='W Price Add'
                                  type='text'
                                  autoComplete='off'
                                  disabled={selProduct.w_amount_add === ''}
                                  value={selProduct.regions && selProduct.regions[`${st.cntry_code}`] ? selProduct.regions[`${st.cntry_code}`].price_w_add : ''}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        );
                      })}
                  </Col>
                )}
                {countries.filter((c) => c.region === 'city').length > 0 && (
                  <Col md={6}>
                    <div className='text-center font-size-18 mt-1 mb-2'>
                      <hr />
                      <b>City</b>
                      <hr />
                    </div>
                    {countries
                      .filter((c) => c.region === 'city')
                      .map((ct, index) => {
                        return (
                          <Row key={index}>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField name={'name_' + ct.cntry_code} label='City' type='text' disabled={true} value={ct.cntry_name} />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField
                                  name={'price_r_' + ct.cntry_code}
                                  label='R Price'
                                  type='text'
                                  autoComplete='off'
                                  value={selProduct.regions && selProduct.regions[`${ct.cntry_code}`] ? selProduct.regions[`${ct.cntry_code}`].price_r : ''}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField
                                  name={'price_w_' + ct.cntry_code}
                                  label='W Price'
                                  type='text'
                                  autoComplete='off'
                                  value={selProduct.regions && selProduct.regions[`${ct.cntry_code}`] ? selProduct.regions[`${ct.cntry_code}`].price_w : ''}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup className='mb-3'>
                                <AvField
                                  name={'price_w_add_' + ct.cntry_code}
                                  label='W Price Add'
                                  type='text'
                                  autoComplete='off'
                                  disabled={selProduct.w_amount_add === ''}
                                  value={selProduct.regions && selProduct.regions[`${ct.cntry_code}`] ? selProduct.regions[`${ct.cntry_code}`].price_w_add : ''}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        );
                      })}
                  </Col>
                )}
              </Row>
              <Row className='prevNext'>
                <Col>
                  <div className='text-end'>
                    <Link
                      to='#'
                      className='btn btn-primary me-3'
                      onClick={() => {
                        toggleTab(activeTab - 1);
                      }}
                    >
                      {' '}
                      Previous
                    </Link>
                    <button className='btn btn-success'>{isEdit ? 'Edit Product' : 'Create Product'}</button>
                  </div>
                </Col>
              </Row>
            </AvForm>
          </TabPane>
        </TabContent>
      </div>
    </div>
  );
  /**
   * Renders the modal content for editing product prices and amounts.
   *
   * The modal includes three rows, each containing three columns with input fields for:
   * - R USD and EUR prices, and R amount
   * - W USD and EUR prices, and W amount
   * - Additional W USD and EUR prices, and additional W amount
   *
   * Each input field is validated for required values and displays appropriate error messages.
   * The values are pre-filled from the selected product's price and amount properties.
   *
   * @constant
   * @type {JSX.Element}
   */
  const modalPriceContent = (
    <>
      <Row>
        <Col md={4}>
          <FormGroup className='mb-3'>
            <AvField
              name='rusd'
              label='R USD Price'
              type='text'
              errorMessage='Invalid Price'
              autoComplete='off'
              validate={{
                required: {
                  value: true
                }
              }}
              value={(selProduct.prices && selProduct.prices.usd.price_r) || ''}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup className='mb-3'>
            <AvField
              name='reur'
              label='R USD Europe Price'
              type='text'
              errorMessage='Invalid Price'
              autoComplete='off'
              validate={{
                required: {
                  value: true
                }
              }}
              value={(selProduct.prices && selProduct.prices.eur.price_r) || ''}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup className='mb-3'>
            <AvField
              name='r_amount'
              label='R Amount'
              placeholder='from 1 to 99'
              type='text'
              errorMessage='Invalid'
              autoComplete='off'
              validate={{
                required: {
                  value: true
                }
              }}
              value={selProduct.r_amount || ''}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <FormGroup className='mb-3'>
            <AvField
              name='wusd'
              label='W USD Price'
              type='text'
              errorMessage='Invalid Price'
              autoComplete='off'
              validate={{
                required: {
                  value: true
                }
              }}
              value={(selProduct.prices && selProduct.prices.usd.price_w) || ''}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup className='mb-3'>
            <AvField
              name='weur'
              label='W USD Europe Price'
              type='text'
              errorMessage='Invalid Price'
              autoComplete='off'
              validate={{
                required: {
                  value: true
                }
              }}
              value={(selProduct.prices && selProduct.prices.eur.price_w) || ''}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup className='mb-3'>
            <AvField
              name='w_amount'
              label='W Amount'
              type='text'
              placeholder='from 100'
              errorMessage='Invalid'
              autoComplete='off'
              validate={{
                required: {
                  value: true
                }
              }}
              value={selProduct.w_amount || ''}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <FormGroup className='mb-3'>
            <AvField
              name='wusd_add'
              label='Additional W USD Price'
              type='text'
              errorMessage='Invalid Price'
              autoComplete='off'
              value={(selProduct.prices && selProduct.prices.usd.price_w_add) || ''}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup className='mb-3'>
            <AvField
              name='weur_add'
              label='Additional W USD Eur Price'
              type='text'
              errorMessage='Invalid Price'
              autoComplete='off'
              value={(selProduct.prices && selProduct.prices.eur.price_w_add) || ''}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup className='mb-3'>
            <AvField name='w_amount_add' label='Additional W Amount' type='text' placeholder='from 300' errorMessage='Invalid' autoComplete='off' value={selProduct.w_amount_add || ''} />
          </FormGroup>
        </Col>
      </Row>
    </>
  );
  /**
   * Renders the modal content for regular price configuration, displaying price fields for regions
   * grouped by Continent, Country, State, and City. Each region group is shown in a separate column
   * if there are corresponding countries available. For each region, the form displays the region name,
   * R Price, W Price, and W Price Add fields, with values pre-filled from the selected product's data.
   * The fields are disabled or enabled based on the product's configuration.
   *
   * @constant
   * @type {JSX.Element}
   * @param {Array<Object>} countries - List of country/region objects with properties such as region, cntry_code, and cntry_name.
   * @param {Object} selProduct - The selected product object containing pricing and region-specific data.
   * @returns {JSX.Element} Modal content with grouped price fields for each region type.
   */
  const modalRegPriceContent = (
    <Row>
      {countries.filter((c) => c.region === 'continent').length > 0 && (
        <Col md={6}>
          <div className='text-center font-size-18 mt-1 mb-2'>
            <hr />
            <b>Continent</b>
            <hr />
          </div>
          {countries
            .filter((c) => c.region === 'continent')
            .sort((a, b) => a.cntry_name.localeCompare(b.cntry_name))
            .map((cont, index) => {
              return (
                <Row key={index}>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField name={'name_' + cont.cntry_code} label='Continent' type='text' disabled={true} value={cont.cntry_name} />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField
                        name={'price_r_' + cont.cntry_code}
                        label='R Price'
                        type='text'
                        autoComplete='off'
                        // value={(selProduct.regions && selProduct.regions[`${cont.cntry_code}`]) ? selProduct.regions[`${cont.cntry_code}`]?.price_r : selProduct.prices?.usd.price_r}
                        value={selProduct.regions?.[cont.cntry_code]?.price_r ?? selProduct.prices?.usd?.price_r}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField
                        name={'price_w_' + cont.cntry_code}
                        label='W Price'
                        type='text'
                        autoComplete='off'
                        value={selProduct.regions?.[cont.cntry_code]?.price_w ?? selProduct.prices?.usd?.price_w}
                        // value={(selProduct.regions && selProduct.regions[`${cont.cntry_code}`]) ? selProduct.regions[`${cont.cntry_code}`].price_w : selProduct.prices?.usd.price_w}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField
                        name={'price_w_add_' + cont.cntry_code}
                        label='W Price Add'
                        type='text'
                        autoComplete='off'
                        disabled={selProduct.w_amount_add === ''}
                        value={selProduct.regions?.[cont.cntry_code]?.price_w_add ?? selProduct.prices?.usd?.price_w_add}
                        // value={(selProduct.regions && selProduct.regions[`${cont.cntry_code}`]) ? selProduct.regions[`${cont.cntry_code}`].price_w_add : selProduct.prices?.usd.price_w_add}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              );
            })}
        </Col>
      )}
      {countries.filter((c) => c.region === 'country').length > 0 && (
        <Col md={6}>
          <div className='text-center font-size-18 mt-1 mb-2'>
            <hr />
            <b>Country</b>
            <hr />
          </div>
          {countries
            .filter((c) => c.region === 'country')
            .sort((a, b) => a.cntry_name.localeCompare(b.cntry_name))
            .map((cntr, index) => {
              return (
                <Row key={index}>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField name={'name_' + cntr.cntry_code} label='Country' type='text' disabled={true} value={cntr.cntry_name} />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField
                        name={'price_r_' + cntr.cntry_code}
                        label='R Price'
                        type='text'
                        autoComplete='off'
                        value={selProduct.regions?.[cntr.cntry_code]?.price_r ?? selProduct.prices?.usd?.price_r}
                        // value={(selProduct.regions && selProduct.regions[`${cntr.cntry_code}`]) ? selProduct.regions[`${cntr.cntry_code}`].price_r : selProduct.prices?.usd.price_r}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField
                        name={'price_w_' + cntr.cntry_code}
                        label='W Price'
                        type='text'
                        autoComplete='off'
                        value={selProduct.regions?.[cntr.cntry_code]?.price_w ?? selProduct.prices?.usd?.price_w}
                        // value={(selProduct.regions && selProduct.regions[`${cntr.cntry_code}`]) ? selProduct.regions[`${cntr.cntry_code}`].price_w : selProduct.prices?.usd.price_w}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField
                        name={'price_w_add_' + cntr.cntry_code}
                        label='W Price Add'
                        type='text'
                        autoComplete='off'
                        disabled={selProduct.w_amount_add === ''}
                        value={selProduct.regions?.[cntr.cntry_code]?.price_w_add ?? selProduct.prices?.usd?.price_w_add}
                        // value={(selProduct.regions && selProduct.regions[`${cntr.cntry_code}`]) ? selProduct.regions[`${cntr.cntry_code}`].price_w_add : selProduct.prices?.usd.price_w_add}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              );
            })}
        </Col>
      )}
      {countries.filter((c) => c.region === 'state').length > 0 && (
        <Col md={6}>
          <div className='text-center font-size-18 mt-1 mb-2'>
            <hr />
            <b>State</b>
            <hr />
          </div>
          {countries
            .filter((c) => c.region === 'state')
            .sort((a, b) => a.cntry_name.localeCompare(b.cntry_name))
            .map((st, index) => {
              return (
                <Row key={index}>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField name={'name_' + st.cntry_code} label='State' type='text' disabled={true} value={st.cntry_name} />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField
                        name={'price_r_' + st.cntry_code}
                        label='R Price'
                        type='text'
                        autoComplete='off'
                        value={selProduct.regions?.[st.cntry_code]?.price_r ?? selProduct.prices?.usd?.price_r}
                        // value={(selProduct.regions && selProduct.regions[`${st.cntry_code}`]) ? selProduct.regions[`${st.cntry_code}`].price_r : selProduct.prices?.usd.price_r}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField
                        name={'price_w_' + st.cntry_code}
                        label='W Price'
                        type='text'
                        autoComplete='off'
                        value={selProduct.regions?.[st.cntry_code]?.price_w ?? selProduct.prices?.usd?.price_w}
                        // value={(selProduct.regions && selProduct.regions[`${st.cntry_code}`]) ? selProduct.regions[`${st.cntry_code}`].price_w : selProduct.prices?.usd.price_w}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField
                        name={'price_w_add_' + st.cntry_code}
                        label='W Price Add'
                        type='text'
                        autoComplete='off'
                        disabled={selProduct.w_amount_add === ''}
                        value={selProduct.regions?.[st.cntry_code]?.price_w_add ?? selProduct.prices?.usd?.price_w_add}
                        // value={(selProduct.regions && selProduct.regions[`${st.cntry_code}`]) ? selProduct.regions[`${st.cntry_code}`].price_w_add : selProduct.prices?.usd.price_w_add}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              );
            })}
        </Col>
      )}
      {countries.filter((c) => c.region === 'city').length > 0 && (
        <Col md={6}>
          <div className='text-center font-size-18 mt-1 mb-2'>
            <hr />
            <b>City</b>
            <hr />
          </div>
          {countries
            .filter((c) => c.region === 'city')
            .sort((a, b) => a.cntry_name.localeCompare(b.cntry_name))
            .map((ct, index) => {
              return (
                <Row key={index}>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField name={'name_' + ct.cntry_code} label='City' type='text' disabled={true} value={ct.cntry_name} />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField
                        name={'price_r_' + ct.cntry_code}
                        label='R Price'
                        type='text'
                        autoComplete='off'
                        value={selProduct.regions?.[ct.cntry_code]?.price_r ?? selProduct.prices?.usd?.price_r}
                        // value={(selProduct.regions && selProduct.regions[`${ct.cntry_code}`]) ? selProduct.regions[`${ct.cntry_code}`].price_r : selProduct.prices?.usd.price_r}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField
                        name={'price_w_' + ct.cntry_code}
                        label='W Price'
                        type='text'
                        autoComplete='off'
                        value={selProduct.regions?.[ct.cntry_code]?.price_w ?? selProduct.prices?.usd?.price_w}
                        // value={(selProduct.regions && selProduct.regions[`${ct.cntry_code}`]) ? selProduct.regions[`${ct.cntry_code}`].price_w : selProduct.prices?.usd.price_w}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className='mb-3'>
                      <AvField
                        name={'price_w_add_' + ct.cntry_code}
                        label='W Price Add'
                        type='text'
                        autoComplete='off'
                        disabled={selProduct.w_amount_add === ''}
                        value={selProduct.regions?.[ct.cntry_code]?.price_w_add ?? selProduct.prices?.usd?.price_w_add}
                        // value={(selProduct.regions && selProduct.regions[`${ct.cntry_code}`]) ? selProduct.regions[`${ct.cntry_code}`].price_w_add : selProduct.prices?.usd.price_w_add}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              );
            })}
        </Col>
      )}
    </Row>
  );
  /**
   * Renders the content for the bulk edit product modal, including product selection,
   * multi-step wizard navigation, and forms for editing general, language, and region-specific
   * product information. The modal supports multi-select for products and regions, animated tab transitions,
   * and dynamic form validation and error feedback.
   *
   * @constant
   * @type {JSX.Element}
   * @description
   * This modal allows users to bulk edit multiple products by providing a wizard interface with three tabs:
   * 1. General Information: Edit product attributes and pricing.
   * 2. Language Info: Manage product details in multiple languages.
   * 3. Region Info: Assign products to regions and set region-specific pricing.
   * The modal includes validation, animated transitions, and custom select components for enhanced UX.
   */
  const modalBulkEditProductContent = (
    <Row>
      <Col md={12}>
        <FormGroup className={showError ? 'text-danger mb-3' : 'mb-3'}>
          <Label>Select Products</Label>
          <Select
            value={selectedMultiProducts}
            isMulti={true}
            onChange={handleMultiProductsSelect}
            options={productOptions}
            classNamePrefix='select2-selection'
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            inputValue={inputValueSelect} // 🔹 Maintain search input
            onInputChange={handleInputSelect} // 🔹 Update input state
            // allowSelectAll={true}
            components={{
              Option: CustomOption,
              GroupHeading: CustomGroupHeadingProduct,
              animatedComponents
            }}
            // components={animatedComponents}
            name='productsSelected'
            id='productsSelected'
            className={showError ? 'is-invalid' : ''}
            styles={{
              control: (base, state) => ({
                ...base,
                borderColor: showError ? '#f46a6a' : base.borderColor,
                boxShadow: showError ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : base.boxShadow,
                '&:hover': {
                  borderColor: showError ? '#f46a6a' : base.borderColor
                }
              }),
              menu: (provided) => ({
                ...provided,
                maxHeight: '80vh', // Adjust the dropdown max height
                overflowY: 'auto'
              }),
              menuList: (provided) => ({
                ...provided,
                maxHeight: '80vh', // Ensures the dropdown does not exceed this height
                overflowY: 'auto'
              })
            }}
          />
          {showError && (!selectedMultiProducts || selectedMultiProducts.length === 0) && <div className='invalid-feedback'>Please select at least one product.</div>}
        </FormGroup>
      </Col>
      <div ref={modalBodyRef} className='wizard clearfix'>
        <div className='steps clearfix'>
          <ul>
            <NavItem className={classnames({ current: activeTab === 1 })}>
              <NavLink
                className={classnames({ active: activeTab === 1 })}
                onClick={() => {
                  toggleTab(1);
                }}
              >
                <span className='number'>1.</span> General Information
              </NavLink>
            </NavItem>
            <NavItem className={classnames({ current: activeTab === 2 })}>
              <NavLink
                className={classnames({ active: activeTab === 2 })}
                onClick={() => {
                  toggleTab(2);
                }}
              >
                <span className='number'>2.</span> <span>Language Info</span>
              </NavLink>
            </NavItem>
            <NavItem className={classnames({ current: activeTab === 3 })}>
              <NavLink
                className={classnames({ active: activeTab === 3 })}
                onClick={() => {
                  toggleTab(3);
                }}
              >
                <span className='number'>3.</span> <span>Region Info</span>
              </NavLink>
            </NavItem>
          </ul>
        </div>
        <div className='content clearfix' style={{ overflowY: 'auto', minHeight: '70vh', maxHeight: '70vh' }}>
          <AnimatePresence mode='wait'>
            {activeTab === 1 && (
              <motion.div
                key='tab-1'
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
                className='tab-content tab-content-1 typography'
              >
                <div>
                  <Row>
                    <Col md='12' className='justify-content-center font-size-18 d-flex mt-2'>
                      <b>General Information</b>
                    </Col>
                  </Row>
                  <hr />
                  <Table className='table' style={{ border: 'none', tableLayout: 'fixed', width: '100%' }}>
                    <tbody>
                      <tr>
                        <td style={{ width: '25%', border: 'none' }}>
                          <AvRadioGroup name='ass_product' value={String(selProduct.ass_product) || ''} onChange={(e) => handleFormChange(e)} className='ms-3 d-flex'>
                            <AvRadio
                              className='mt-2'
                              label={
                                <>
                                  <i className='mdi mdi-multiplication-box mdi-18px' />
                                  <span className='font-size-16'>Mark as Assortment Product</span>
                                </>
                              }
                              value='true'
                            />
                            <AvRadio
                              className='mt-2'
                              label={
                                <>
                                  <i className='mdi mdi-checkbox-blank mdi-18px' />
                                  <span className='font-size-16'>Unmark as Assortment Product</span>
                                </>
                              }
                              value='false'
                            />
                          </AvRadioGroup>
                        </td>
                        <td style={{ width: '25%', border: 'none' }}>
                          <AvRadioGroup name='hidden' value={String(selProduct.hidden) || ''} onChange={(e) => handleFormChange(e)} className='ms-3 font-size-18 d-flex'>
                            <AvRadio
                              className='mt-2'
                              label={
                                <>
                                  <i className='mdi mdi-eye-off mdi-24px' /> <span className='font-size-16'>Hide Product</span>
                                </>
                              }
                              value='true'
                            />
                            <AvRadio
                              className='mt-2'
                              label={
                                <>
                                  <i className='mdi mdi-eye mdi-24px' /> <span className='font-size-16'>Unhide Product</span>
                                </>
                              }
                              value='false'
                            />
                          </AvRadioGroup>
                        </td>
                        <td style={{ width: '25%', border: 'none' }}>
                          <AvRadioGroup name='best_seller' value={String(selProduct.best_seller) || ''} onChange={(e) => handleFormChange(e)} className='ms-3 font-size-18 d-flex'>
                            <AvRadio
                              className='mt-2'
                              label={
                                <>
                                  <i className='mdi mdi-bookmark-check mdi-24px' /> <span className='font-size-16'>Mark as Best Seller</span>
                                </>
                              }
                              value='true'
                            />
                            <AvRadio
                              className='mt-2'
                              label={
                                <>
                                  <i className='mdi mdi-bookmark-minus mdi-24px' /> <span className='font-size-16'>Unmark as Best Seller</span>
                                </>
                              }
                              value='false'
                            />
                          </AvRadioGroup>
                        </td>
                        <td style={{ width: '25%', border: 'none' }}>
                          <AvRadioGroup name='new_product' value={String(selProduct.new_product) || ''} onChange={(e) => handleFormChange(e)} className='ms-3 font-size-18 d-flex'>
                            <AvRadio
                              className='mt-2'
                              label={
                                <>
                                  <i className='mdi mdi-new-box mdi-24px' /> <span className='font-size-16'>Mark as New Product</span>
                                </>
                              }
                              value='true'
                            />
                            <AvRadio
                              className='mt-2'
                              label={
                                <>
                                  <i className='mdi mdi-new-box mdi-24px' /> <span className='font-size-16'>Unmark as New Product</span>
                                </>
                              }
                              value='false'
                            />
                          </AvRadioGroup>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                  <hr />
                  <Row>
                    <Col md='12' className='justify-content-center font-size-18 d-flex mt-1 mb-1'>
                      <b>Price Information</b>
                    </Col>
                  </Row>
                  <hr />
                  <Row>
                    <Col md={4}>
                      <FormGroup className='mb-3'>
                        <AvField name='rusd' label='R USD Price' type='text' errorMessage='Invalid Price' autoComplete='off' onChange={onChangeRUSD} value={selProduct.prices?.usd?.price_r || ''} />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup className='mb-3'>
                        <AvField
                          name='reur'
                          label='R USD Europe Price'
                          type='text'
                          errorMessage='Invalid Price'
                          autoComplete='off'
                          onChange={(e) => handleFormChange(e)}
                          value={selProduct.prices?.eur?.price_r || ''}
                          validate={
                            rusdFilled === ''
                              ? {
                                  required: {
                                    value: false
                                  }
                                }
                              : {
                                  required: {
                                    value: true
                                  }
                                }
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup className='mb-3'>
                        <AvField
                          name='r_amount'
                          label='R Amount'
                          placeholder='from 1 to 99'
                          type='text'
                          errorMessage='Invalid'
                          autoComplete='off'
                          onChange={(e) => handleFormChange(e)}
                          value={selProduct.r_amount || ''}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <FormGroup className='mb-3'>
                        <AvField
                          name='wusd'
                          label='W USD Price'
                          type='text'
                          errorMessage='Invalid Price'
                          autoComplete='off'
                          onChange={(e) => handleFormChange(e)}
                          value={selProduct.prices?.usd?.price_w || ''}
                          validate={
                            rusdFilled === ''
                              ? {
                                  required: {
                                    value: false
                                  }
                                }
                              : {
                                  required: {
                                    value: true
                                  }
                                }
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup className='mb-3'>
                        <AvField
                          name='weur'
                          label='W USD Europe Price'
                          type='text'
                          errorMessage='Invalid Price'
                          autoComplete='off'
                          onChange={(e) => handleFormChange(e)}
                          value={selProduct.prices?.eur?.price_w || ''}
                          validate={
                            rusdFilled === ''
                              ? {
                                  required: {
                                    value: false
                                  }
                                }
                              : {
                                  required: {
                                    value: true
                                  }
                                }
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup className='mb-3'>
                        <AvField
                          name='w_amount'
                          label='W Amount'
                          type='text'
                          placeholder='from 100'
                          errorMessage='Invalid'
                          autoComplete='off'
                          onChange={(e) => handleFormChange(e)}
                          value={selProduct.w_amount || ''}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <FormGroup className='mb-3'>
                        <AvField
                          name='wusd_add'
                          label='Additional W USD Price'
                          type='text'
                          errorMessage='Invalid Price'
                          autoComplete='off'
                          onChange={(e) => handleFormChange(e)}
                          value={selProduct.prices?.usd?.price_w_add || ''}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup className='mb-3'>
                        <AvField
                          name='weur_add'
                          label='Additional W USD Eur Price'
                          type='text'
                          errorMessage='Invalid Price'
                          autoComplete='off'
                          onChange={(e) => handleFormChange(e)}
                          value={selProduct.prices?.eur?.price_w_add || ''}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup className='mb-3'>
                        <AvField
                          name='w_amount_add'
                          label='Additional W Amount'
                          type='text'
                          placeholder='from 300'
                          errorMessage='Invalid'
                          autoComplete='off'
                          onChange={(e) => handleFormChange(e)}
                          value={selProduct.w_amount_add || ''}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </motion.div>
            )}
            {activeTab === 2 && (
              <motion.div
                key='tab-2'
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
                className='tab-content tab-content-2 typography'
              >
                <div>
                  <Row>
                    <Col md='12' className='justify-content-center font-size-18 d-flex mt-2'>
                      <b>Manage Products In Languages</b>
                    </Col>
                  </Row>
                  <hr />
                  <Row>
                    <Col md={12} className='justify-content-center font-size-18 d-flex align-items-baseline flex-wrap mt-2'>
                      {languages.map((lng, index) => (
                        <div className='alert alert-success d-flex align-items-baseline flex-wrap m-2 mng_languages' key={index}>
                          {lng.lng_name}
                          <AvRadioGroup name={lng.lng_code} value={String(selProduct.languages?.[lng.lng_code]?.display) || ''} onChange={(e) => handleFormChange(e)} className='ms-3 d-flex'>
                            <AvRadio
                              className='mt-2'
                              label={
                                <>
                                  <i className='mdi mdi mdi-eye mdi-18px' />
                                </>
                              }
                              value='true'
                            />
                            <AvRadio
                              className='mt-2'
                              label={
                                <>
                                  <i className='mdi mdi-eye-off mdi-18px' />
                                </>
                              }
                              value='false'
                            />
                          </AvRadioGroup>
                        </div>
                      ))}
                    </Col>
                  </Row>

                  <Row>
                    {languages.map((lng, index) => (
                      <>
                        <div className='text-center font-size-18 mt-1 mb-2'>
                          <hr />
                          <b>Edit Information in {lng.lng_name}</b>
                          <hr />
                        </div>
                        <Row>
                          <Col md={6}>
                            <FormGroup className='mb-3'>
                              <AvField
                                name={`title_${lng.lng_code}`}
                                label='Title'
                                type='text'
                                onChange={(e) => handleFormChange(e)}
                                value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].title : ''}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={2}>
                            <FormGroup className='mb-3'>
                              <AvField
                                name={`needle_type_${lng.lng_code}`}
                                label='Needle Type'
                                type='text'
                                autoComplete='off'
                                onChange={(e) => handleFormChange(e)}
                                value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].needle_type : ''}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={2}>
                            <FormGroup className='mb-3'>
                              <AvField
                                name={`needle_gage_${lng.lng_code}`}
                                label='Needle Gage'
                                type='text'
                                autoComplete='off'
                                onChange={(e) => handleFormChange(e)}
                                value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].needle_gage : ''}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={2}>
                            <FormGroup className='mb-3'>
                              <AvField
                                name={`needle_mm_${lng.lng_code}`}
                                label='Needle MM'
                                type='text'
                                autoComplete='off'
                                onChange={(e) => handleFormChange(e)}
                                value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].needle_mm : ''}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={2}>
                            <FormGroup className='mb-3'>
                              <AvField
                                name={`thread_type_${lng.lng_code}`}
                                label='Thread Type'
                                type='text'
                                autoComplete='off'
                                onChange={(e) => handleFormChange(e)}
                                value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].thread_type : ''}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={2}>
                            <FormGroup className='mb-3'>
                              <AvField
                                name={`thread_gage_${lng.lng_code}`}
                                label='Thread Gage'
                                type='text'
                                autoComplete='off'
                                onChange={(e) => handleFormChange(e)}
                                value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].thread_gage : ''}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={2}>
                            <FormGroup className='mb-3'>
                              <AvField
                                name={`thread_mm_${lng.lng_code}`}
                                label='Thread MM'
                                type='text'
                                autoComplete='off'
                                onChange={(e) => handleFormChange(e)}
                                value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].thread_mm : ''}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className='mb-3'>
                              <AvField
                                name={`composition_${lng.lng_code}`}
                                label='Composition'
                                type='textarea'
                                autoComplete='off'
                                className='font-size-16'
                                rows='5'
                                onChange={(e) => handleFormChange(e)}
                                value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].composition : ''}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className='mb-3'>
                              <AvField
                                name={`description_${lng.lng_code}`}
                                label='Description'
                                type='textarea'
                                autoComplete='off'
                                className='font-size-16'
                                rows='5'
                                onChange={(e) => handleFormChange(e)}
                                value={selProduct.languages && selProduct.languages[`${lng.lng_code}`] ? selProduct.languages[`${lng.lng_code}`].description : ''}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </>
                    ))}
                  </Row>
                </div>
              </motion.div>
            )}
            {activeTab === 3 && (
              <motion.div
                key='tab-3'
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
                className='tab-content tab-content-3 typography'
              >
                <div>
                  <Row>
                    <Col md={12}>
                      <FormGroup className={showRegionsError ? 'text-danger mb-3' : 'mb-3'}>
                        <Select
                          value={selectedMultiRegions}
                          isMulti={true}
                          onChange={handleMultiRegionsSelect}
                          options={regionOptions}
                          classNamePrefix='select2-selection'
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          // allowSelectAll={true}
                          components={{
                            Option: CustomOption,
                            GroupHeading: CustomGroupHeadingRegion,
                            animatedComponents
                          }}
                          // components={animatedComponents}
                          name='regionsSelected'
                          id='regionsSelected'
                          className={showRegionsError ? 'is-invalid' : ''}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              borderColor: showRegionsError ? '#f46a6a' : base.borderColor,
                              boxShadow: showRegionsError ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : base.boxShadow,
                              '&:hover': {
                                borderColor: showRegionsError ? '#f46a6a' : base.borderColor
                              }
                            })
                          }}
                        />
                        {showRegionsError && (!selectedMultiRegions || selectedMultiRegions.length === 0) && <div className='invalid-feedback'>Please select at least one region.</div>}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <FormGroup className='mb-3'>
                        <AvField
                          name='price_r'
                          label='Text'
                          type='text'
                          validate={{
                            ...((
                              (formValues.assign_default === undefined || formValues.assign_default === false) &&
                              (formValues.assign_default_eur === undefined || formValues.assign_default_eur === false)
                            ) && {
                              required: {
                                value: true,
                                errorMessage: 'This field is required'
                              },
                              noInvalidPhrase: (val) => {
                                if (val.includes('(Keep it empty if you want to remove product from the region)')) {
                                  return 'Please remove "(Keep it empty if you want to remove product from the region)"';
                                }
                                return true;
                              }
                            })
                          }}
                          invalid={showPriceError}
                          onChange={(e) => handleFormChange(e)}
                          value={formValues.price_r !== undefined ? formValues.price_r : 'You can purchase from our official distributor (Keep it empty if you want to remove product from the region)'}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <FormGroup className='mb-3'>
                        <AvField name='assign_default' type='checkbox' className='form-check-input ' onChange={(e) => handleFormChange(e)} label='Assign Default Prices to Selected Regions' />
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup className='mb-3'>
                        <AvField name='assign_default_eur' type='checkbox' className='form-check-input ' onChange={(e) => handleFormChange(e)} label='Assign European Prices to Selected Regions' />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </Row>
  );
  /**
   * Renders the modal content for exporting the price list as PDF.
   *
   * This modal provides three main sections:
   * - General Price List: Allows users to select a default price list and language.
   * - Regional Price List: Enables filtering by continent, country, state, and city.
   * - Additional Filters: Offers category selection and options to include/exclude wholesale prices and assortment products.
   *
   * The modal uses AvField and AvCheckbox components for form controls, and dynamically populates select options based on provided regions, countries, languages, and categories.
   *
   * @constant
   * @type {JSX.Element}
   */
  const modalExportPDFContent = (
    <>
      <Row>
        <Col md={4}>
          <div className='text-center font-size-18 mt-2 mb-5'>
            <b>Download General Price List</b>
          </div>
          <Row>
            <Col md={12}>
              <FormGroup className='mb-3'>
                <AvField
                  type='select'
                  name='expDef'
                  className='form-select'
                  label='Default Price List'
                  value={regions.expDef}
                  onChange={handleRegionsChange}
                  disabled={isDisabled('expCont', 'expCntr', 'expSt', 'expCt')}
                >
                  <option value=''>Please Select Region & Currency</option>
                  <option value='usd'> World (USD) </option>
                  <option value='eur'> Europe (EUR) </option>
                </AvField>
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup className='mb-3'>
                <AvField
                  type='select'
                  name='expLng'
                  className='form-select'
                  label='Language'
                  value={regions.expLng != '' && regions.expLng}
                  onChange={handleRegionsChange}
                  validate={{
                    required: {
                      value: true
                    }
                  }}
                >
                  <option value=''>Please Select Language</option>
                  {languages.map((lng, index) => {
                    return (
                      <option key={index} value={lng.lng_code}>
                        {lng.lng_name}
                      </option>
                    );
                  })}
                </AvField>
              </FormGroup>
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <div className='text-center font-size-18 mt-2 mb-5'>
            <b>Download Regional Price List</b>
          </div>
          <Row>
            <Col md={12}>
              <FormGroup className='mb-3'>
                <AvField type='select' name='expCont' className='form-select' label='By Continent' onChange={handleRegionsChange} disabled={isDisabled('expDef', 'expCntr', 'expSt', 'expCt')}>
                  <option value=''>Please Select Continent</option>
                  {countries
                    .filter((c) => c.region === 'continent')
                    .sort((a, b) => a.cntry_name.localeCompare(b.cntry_name))
                    .map((cntr, index) => {
                      return (
                        <option key={index} value={cntr.cntry_code}>
                          {cntr.cntry_name}
                        </option>
                      );
                    })}
                </AvField>
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup className='mb-3'>
                <AvField type='select' name='expCntr' className='form-select' label='By Country' onChange={handleRegionsChange} disabled={isDisabled('expDef', 'expCont', 'expSt', 'expCt')}>
                  <option value=''>Please Select Country</option>
                  {countries
                    .filter((c) => c.region === 'country')
                    .sort((a, b) => a.cntry_name.localeCompare(b.cntry_name))
                    .map((cntr, index) => {
                      return (
                        <option key={index} value={cntr.cntry_code}>
                          {cntr.cntry_name}
                        </option>
                      );
                    })}
                </AvField>
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup className='mb-3'>
                <AvField type='select' name='expSt' className='form-select' label='By State' onChange={handleRegionsChange} disabled={isDisabled('expDef', 'expCont', 'expCntr', 'expCt')}>
                  <option value=''>Please Select State</option>
                  {countries
                    .filter((c) => c.region === 'state')
                    .sort((a, b) => a.cntry_name.localeCompare(b.cntry_name))
                    .map((cntr, index) => {
                      return (
                        <option key={index} value={cntr.cntry_code}>
                          {cntr.cntry_name}
                        </option>
                      );
                    })}
                </AvField>
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup className='mb-3'>
                <AvField type='select' name='expCt' className='form-select' label='By City' onChange={handleRegionsChange} disabled={isDisabled('expDef', 'expCont', 'expCntr', 'expSt')}>
                  <option value=''>Please Select City</option>
                  {countries
                    .filter((c) => c.region === 'city')
                    .sort((a, b) => a.cntry_name.localeCompare(b.cntry_name))
                    .map((cntr, index) => {
                      return (
                        <option key={index} value={cntr.cntry_code}>
                          {cntr.cntry_name}
                        </option>
                      );
                    })}
                </AvField>
              </FormGroup>
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <div className='text-center font-size-18 mt-2 mb-5'>
            <b>Additional Filters</b>
          </div>
          <Row>
            {/* <Col md={12}>
            <FormGroup className='mb-3'>
              <AvField
                type='select'
                name='expLng'
                className='form-select'
                label='Region Language'
                value={regions.expLng != '' && regions.expLng}
                onChange={handleRegionsChange}
                validate={{
                  required: {
                    value: true
                  }
                }}
              >
                <option value=''>Please Select Region Language</option>
                {languages.map((lng, index) => {
                  return (
                    <option key={index} value={lng.lng_code}>
                      {lng.lng_name}
                    </option>
                  )
                })}
              </AvField>
            </FormGroup>
          </Col> */}
            <Col md={12}>
              <FormGroup className='mb-3'>
                <AvField type='select' name='expCat' className='form-select' label='Select Category' onChange={handleRegionsChange} size={categories.length} multiple>
                  {categories &&
                    categories.map((category, index) => {
                      return (
                        <option value={category._id} key={index}>
                          {category.catNameLng?.catName}
                        </option>
                      );
                    })}
                </AvField>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className='mt-5 font-size-18'>
                <AvCheckboxGroup name='includeWAmount'>
                  <AvCheckbox label='Include Wholesale Price' checked={addFilters.includeWAmount} onChange={handleAddFilterChange} />
                </AvCheckboxGroup>
              </FormGroup>
            </Col>
            <Col md={6}>
              {/* <FormGroup className='mt-5 font-size-18'>
                <AvCheckboxGroup name='includeWAmountAdd'>
                  <AvCheckbox label='Include Add Wh Price' checked={addFilters.includeWAmountAdd} onChange={handleAddFilterChange} />
                </AvCheckboxGroup>
              </FormGroup> */}
            </Col>
            <Col md={12}>
              <FormGroup className='mt-5 font-size-18'>
                <AvCheckboxGroup name='assProducts'>
                  <AvCheckbox
                    label={
                      <>
                        Download Assortment Products Only. <br /> (Check it if you need to export Assortment products only)
                      </>
                    }
                    checked={addFilters.assProducts}
                    onChange={handleAddFilterChange}
                  />
                </AvCheckboxGroup>
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup className='mt-5 font-size-18'>
                <AvCheckboxGroup name='noAssProducts'>
                  <AvCheckbox
                    label={
                      <>
                        Exclude Assortment Products from Price List. <br /> (It will exclude Assortment Products from Price List. <br /> Uncheck it if you want to export All products )
                      </>
                    }
                    checked={addFilters.noAssProducts}
                    onChange={handleAddFilterChange}
                  />
                </AvCheckboxGroup>
              </FormGroup>
            </Col>
          </Row>
        </Col>
      </Row>
      <hr />
    </>
  );
  /**
   * Formats and returns a category name as a styled React element based on the provided language.
   *
   * @callback catNameFormatter
   * @param {*} cell - The cell value (unused in this formatter).
   * @param {Object} category - The category object containing name translations.
   * @param {number} _rowIndex - The row index (unused in this formatter).
   * @param {Object} options - Additional options.
   * @param {string} options.lng - The language code used to select the category name.
   * @returns {JSX.Element} A styled <h5> element displaying the formatted category name.
   */
  const catNameFormatter = useCallback((cell, category, _rowIndex, { lng }) => {
    const capitalizedLng = lng.charAt(0).toUpperCase() + lng.slice(1);
    const key = `catName${capitalizedLng}`;
    const catName = key === 'catNameEng' ? category.catNameLng?.catName : category.catNameLng?.[key];

    return (
      <h5 className='font-size-20 mb-1'>
        <b>{catName}</b>
      </h5>
    );
  }, []);
  /**
   * Defines the column configuration for a price list table.
   * Each column object specifies display text, data field mapping, sorting and export options,
   * visibility, editability, and custom formatting logic.
   * The configuration is memoized and updates when the language (`lng`) changes.
   *
   * @returns {Array<Object>} Array of column configuration objects for the price list table.
   */
  const columns = useMemo(
    () => [
      {
        text: 'SORT NO',
        dataField: 'sortNo',
        sort: true,
        csvExport: true,
        hidden: false,
        editable: false,
        formatter: (cellContent, category) => (
          <h5 className='font-size-20 mb-1'>
            <b>{category.sortNo}</b>
          </h5>
        )
      },
      {
        text: 'CATEGORY NAME',
        dataField: 'catName',
        sort: true,
        csvExport: true,
        hidden: false,
        editable: false,
        csvFormatter: (cell, row, rowIndex) => row.catNameLng?.catName,
        formatter: catNameFormatter,
        formatExtraData: { lng }
      }
    ],
    [lng]
  );
  /**
   * Configuration object for rendering an expandable row in a price list table.
   *
   * @typedef {Object} ExpandRow
   * @property {string} parentClassName - CSS class applied to the parent row when expanded.
   * @property {function(Object): JSX.Element} renderer - Function that renders the expanded row content as a table, displaying product details based on category and language.
   * @property {boolean} showExpandColumn - Whether to show the expand/collapse column in the table.
   * @property {boolean} onlyOneExpanding - If true, only one row can be expanded at a time.
   *
   * @description
   * The `expandRow` object configures how a row in the price list table expands to show detailed product information.
   * The `renderer` function generates a table with dynamic columns and rows based on the product's category, language, and region.
   * It supports conditional rendering for special categories (e.g., "COG Threads"), displays product images, prices, and allows actions such as edit and delete if the user has access.
   */
  const expandRow = {
    parentClassName: 'completed-row',
    renderer: (row) => {
      return (
        <Table className='table text-center mt-4 ms-0 me-0 mb-4'>
          <thead>
            <tr>
              <th>No</th>
              <th>MODEL NAME</th>
              <th>PRODUCT DRAWING</th>
              {row?.catNameLng?.catName !== 'COG Threads' && row?.catNameLng?.catName !== 'Devices' && <th>COMPOSITION & PACK.UNIT</th>}
              {row?.catNameLng?.catName === 'COG Threads' && <th>NEEDLE TYPE</th>}
              {row?.catNameLng?.catName === 'COG Threads' && <th>NEEDLE GAUGE</th>}
              {row?.catNameLng?.catName === 'COG Threads' && <th>NEEDLE MM</th>}
              {row?.catNameLng?.catName === 'COG Threads' && <th>THREAD TYPE</th>}
              {row?.catNameLng?.catName === 'COG Threads' && <th>NEEDLE USP</th>}
              {row?.catNameLng?.catName === 'COG Threads' && <th>NEEDLE MM</th>}
              <th>R PRICE</th>
              <th>W PRICE</th>
              {row.w_amount_add !== '' && <th>W PRICE Add</th>}
              {row?.catNameLng?.catName !== 'COG Threads' && <th>DESCRIPTION</th>}
              {profileData.permissions.includes('priceLstAccess') && <th>Action</th>}
            </tr>
          </thead>
          <tbody>
            {products
              .filter((product) => product.category?._id === row._id)
              .sort((a, b) => a.sortNo - b.sortNo)
              .map((product, index) => {
                const productLanguage = product.languages?.[lng];
                const productRegion = region === '' || (product.regions && product.regions[region]);
                if (productLanguage && productRegion && productLanguage.display === true) {
                  return (
                    <tr key={index} className={`${product.hidden && 'on-hold-row'} ${product.ass_product && 'ass-prod'}`}>
                      <td>
                        <h5 className='font-size-16 mb-1'>{product.sortNo}</h5>
                      </td>
                      <td className='name-width'>
                        <div className='comp-text text-center'>
                          <h5 className='font-size-16 mb-1'>
                            {product.best_seller && <img className='product-bs' src={best_seller_img} alt='' />}
                            {product.new_product && <img className='product-bs' src={best_seller_img} alt='' />}
                            {product.languages[lng] && product.languages[lng].title && product.languages[lng].title}
                          </h5>
                        </div>
                        {profileData.permissions.includes('priceLstAccess') &&
                          languages.map((lng, index) => {
                            const langData = product.languages[lng.lng_code];
                            const isSpecialCategory = product.category._id === '647004abb4fb05147748ca68';
                            const isFilled =
                              langData &&
                              (isSpecialCategory ? !!langData.title && !!langData.needle_type : (!!langData.title && !!langData.composition) || (!!langData.title && !!langData.description));

                            return (
                              <div className={isFilled && langData.display === true ? 'alert alert-success d-flex' : 'alert alert-danger d-flex'} key={index}>
                                <p className='pe-1 mb-0'>
                                  {lng.lng_name}: <b>{isFilled ? 'FILLED' : 'NOT FILLED'}</b>
                                </p>
                                <p className={isFilled ? 'text-success' : 'text-danger'}>
                                  {isFilled && lng.lng_code !== 'eng' && (
                                    <Link className='text-primary ms-1' to='#'>
                                      <i
                                        className={langData.display === true ? 'mdi mdi-eye font-size-18 text-success' : 'mdi mdi-eye-off font-size-18 text-danger'}
                                        onClick={(e) => {
                                          displayLangProduct(langData.display === true ? 'hide' : 'display', lng.lng_code, product);
                                        }}
                                      ></i>
                                    </Link>
                                  )}
                                </p>
                              </div>
                            );
                          })}
                      </td>
                      <td>
                        <img className='product-av' src={product.pic} alt='' />
                      </td>
                      {row?.catNameLng?.catName !== 'COG Threads' && row?.catNameLng?.catName !== 'Devices' && (
                        <td>
                          <div className='comp-text text-center'>
                            {product.languages[lng] && product.languages[lng].composition !== null && product.languages[lng].composition && (
                              <h5 className='font-size-16 mb-1'>{product.languages[lng].composition}</h5>
                            )}
                          </div>
                        </td>
                      )}
                      {row?.catNameLng?.catName === 'COG Threads' && (
                        <td>
                          {' '}
                          <h5 className='font-size-16 mb-1'>{product.languages[lng] ? product.languages[lng].needle_type : product.languages['eng'].needle_type}</h5>{' '}
                        </td>
                      )}
                      {row?.catNameLng?.catName === 'COG Threads' && (
                        <td>
                          {' '}
                          <h5 className='font-size-16 mb-1'>{product.languages[lng] ? product.languages[lng].needle_gage : product.languages['eng'].needle_gage}</h5>{' '}
                        </td>
                      )}
                      {row?.catNameLng?.catName === 'COG Threads' && (
                        <td>
                          {' '}
                          <h5 className='font-size-16 mb-1'>{product.languages[lng] ? product.languages[lng].needle_mm : product.languages['eng'].needle_mm}</h5>{' '}
                        </td>
                      )}
                      {row?.catNameLng?.catName === 'COG Threads' && (
                        <td>
                          {' '}
                          <h5 className='font-size-16 mb-1'>{product.languages[lng] ? product.languages[lng].thread_type : product.languages['eng'].thread_type}</h5>{' '}
                        </td>
                      )}
                      {row?.catNameLng?.catName === 'COG Threads' && (
                        <td>
                          {' '}
                          <h5 className='font-size-16 mb-1'>{product.languages[lng] ? product.languages[lng].thread_gage : product.languages['eng'].thread_gage}</h5>{' '}
                        </td>
                      )}
                      {row?.catNameLng?.catName === 'COG Threads' && (
                        <td>
                          {' '}
                          <h5 className='font-size-16 mb-1'>{product.languages[lng] ? product.languages[lng].thread_mm : product.languages['eng'].thread_mm}</h5>{' '}
                        </td>
                      )}
                      <td>
                        <h5 className='font-size-16 mb-1'>
                          {(() => {
                            const price = region ? product.regions[region]?.price_r : product.prices?.usd?.price_r;
                            return getCurrencySymbol(region, price) + price;
                          })()}
                        </h5>
                      </td>
                      <td colSpan={product.w_amount_add === '' ? 2 : 0}>
                        <h5 className='font-size-16 mb-1'>
                          {(() => {
                            const price = region ? product.regions[region]?.price_w : product.prices?.usd?.price_w;
                            return getCurrencySymbol(region, price) + price;
                          })()}
                        </h5>
                      </td>
                      {product.w_amount_add !== '' && (
                        <td>
                          <h5 className='font-size-16 mb-1'>
                            {(() => {
                              const price = region ? product.regions[region]?.price_w_add : product.prices?.usd?.price_w_add;
                              return getCurrencySymbol(region, price) + price;
                            })()}
                          </h5>
                        </td>
                      )}
                      {row?.catNameLng?.catName !== 'COG Threads' && (
                        <td>
                          <div className='comp-text text-center'>
                            {product.languages[lng] && product.languages[lng].description !== null && product.languages[lng].description && (
                              <h5 className='font-size-16 mb-1'>{product.languages[lng].description}</h5>
                            )}
                          </div>
                        </td>
                      )}
                      {profileData.permissions.includes('priceLstAccess') && (
                        <td>
                          <div className='d-flex gap-3'>
                            <Link className='text-warning' to='#'>
                              <i
                                className='mdi mdi-pencil font-size-18'
                                title='Edit Product'
                                onClick={(e) => {
                                  handleEditProductClick(e, product);
                                }}
                              ></i>
                            </Link>
                            <Link className='text-danger' to='#'>
                              <i className='mdi mdi-delete font-size-18' title='Delete Product' onClick={() => handleProductClickDelete(product)}></i>
                            </Link>
                          </div>
                          <div className='d-flex gap-3'>
                            <Link className='text-success' to='#'>
                              <i
                                className='mdi mdi-currency-usd font-size-18'
                                title='Edit Default Prices'
                                onClick={(e) => {
                                  handleEditPriceClick(e, product);
                                }}
                              ></i>
                            </Link>
                            <Link className='text-info' to='#'>
                              <i
                                className='mdi mdi-currency-gbp font-size-18'
                                title='Edit Regional Prices'
                                onClick={(e) => {
                                  handleEditRegPriceClick(e, product);
                                }}
                              ></i>
                            </Link>
                          </div>
                          <div className='d-flex gap-3'>
                            <Link className='text-primary' to='#'>
                              <i
                                className={product.hidden === false ? 'mdi mdi-eye font-size-18' : 'mdi mdi-eye-off font-size-18'}
                                onClick={(e) => {
                                  hideProduct(product.hidden === false ? 'hide' : 'show', product);
                                }}
                              ></i>
                            </Link>
                            <Link className='text-primary' to='#'>
                              <i
                                className={product.ass_product === false ? 'mdi mdi-checkbox-blank font-size-18' : 'mdi mdi-multiplication-box font-size-18'}
                                onClick={(e) => {
                                  assProduct(product.ass_product === false ? 'ass' : 'not_ass', product);
                                }}
                              ></i>
                            </Link>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                }
              })}
          </tbody>
        </Table>
      );
    },
    showExpandColumn: true,
    onlyOneExpanding: true
  };
  /**
   * Updates the display status of a specific language for a given product.
   *
   * @param {string} option - Determines whether to hide or show the language ('hide' to hide, any other value to show).
   * @param {string} language - The language key to update in the product's languages object.
   * @param {Object} product - The product object containing language display information.
   */
  const displayLangProduct = (option, language, product) => {
    product['languages'][language].display = option === 'hide' ? false : true;
    // onEditPriceProduct(product);
  };

  const defaultSorted = [
    {
      dataField: 'sortNo', // if dataField is not match to any column you defined, it will be ignored.
      order: 'asc' // desc or asc
    }
  ];

  const lngOptions = languages.map(({ lng_code, lng_name }) => ({
    key: lng_code,
    label: lng_name,
    icon: '',
    colorClass: 'bg-primary'
  }));

  const filterFormValues = { languages: ['eng'] };

  return (
    <React.Fragment>
      <PageLoader progress={progress} step={step} visible={visible} />
      <div className='page-content'>
        <MetaTags>
          <title>Price List</title>
        </MetaTags>
        <Container fluid>
          {profileData.permissions.includes('priceLstAccessView') ? (
            <>
              <Breadcrumbs title='Prices' breadcrumbItem='Price List' />
              <Row className='card-section'>
                <Col lg='12'>
                  <div className='pcss3t logtb pcss3t-effect-scale pcss3t-theme-1'>
                    <ul>
                      <AnimatePresence key='1' exitBeforeEnter>
                        <motion.li
                          className={`tab-content tab-content-1 typography`}
                          initial={{ opacity: 0, x: 30 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -30 }}
                          transition={{ duration: 0.3 }}
                          style={{ display: 'block' }}
                        >
                          <div id='card-section' ref={cardRef}>
                            <Row className='card-section'>
                              <Col lg='12'>
                                <Card>
                                  <CardBody className='cardBodySticky'>
                                    {!isLoading && columns && (
                                      <ToolkitProvider keyField='_id' columns={columns} data={categories} search>
                                        {(props) => (
                                          <React.Fragment>
                                            <MainContent
                                              page={{
                                                pg: 'prices',
                                                key: lng,
                                                props: props,
                                                expandRow: expandRow,
                                                cardHeight,
                                                showSearch: true,
                                                showCSV: true,
                                                showInTblFltr: true,
                                                showDbFilter: false,
                                                showBtn: true,
                                                showScndBtn: true,
                                                showThrdBtn: true,
                                                columns: props.baseProps.columns,
                                                data: props.baseProps.data,
                                                defaultSorted
                                              }}
                                              filterOptions={{ languages: lngOptions }}
                                              filterDbOptions={{ filterFormValues, filterCustomContent, singleSelect: true, allowToggleSingle: true }}
                                              setNoDbFilters={setNoDbFilters}
                                              btnCrtModalOptions={{
                                                btnLabelCrt: 'Add New Product',
                                                // btnIcon: 'mdi mdi-link-variant-plus me-1',
                                                modalSize: 'xl',
                                                modalTitle: isEdit ? 'Edit Product' : 'Add Product',
                                                modalBtnLabel: isEdit ? 'Edit Product' : 'Add Product',
                                                modalBtnLabelDisplay: false,
                                                modalAdd: modalNewProduct,
                                                toggleAddModal: toggleNewProductModal,
                                                handleNewClick: handleNewProductClick,
                                                modalContent: modalNewProductContent,
                                                handleSubmit: null
                                              }}
                                              btnCrtScndModalOptions={{
                                                btnLabelCrtScnd: 'Bulk Edit Products',
                                                btnColorScnd: 'warning',
                                                btnIconScnd: 'mdi mdi-vector-polyline-edit me-1',
                                                modalSizeScnd: 'xl',
                                                modalTitleScnd: 'Manage Bulk Edit Products',
                                                modalBtnLabelScnd: 'Apply Changes',
                                                modalAddScnd: modalBulkEdit,
                                                toggleAddModalScnd: toggleBulkEditModal,
                                                handleNewClickScnd: handleBulkEditProductClick,
                                                modalContentScnd: modalBulkEditProductContent,
                                                handleSubmitScnd: handleBulkEditSubmit
                                                // errorMsgScnd: error
                                              }}
                                              btnCrtThrdModalOptions={{
                                                btnLabelCrtThrd: 'Download PDF',
                                                // btnColorThrd: 'info',
                                                btnIconThrd: 'mdi mdi-file-pdf-box me-1',
                                                modalSizeThrd: 'xl',
                                                modalTitleThrd: 'Prepare PDF',
                                                modalBtnLabelThrd: 'Download PDF',
                                                modalAddThrd: modalExportPDF,
                                                toggleAddModalThrd: toggleExportPDFModal,
                                                handleNewClickThrd: handleExportPDFClick,
                                                modalContentThrd: modalExportPDFContent,
                                                handleSubmitThrd: handleExportPDFSubmit
                                                // errorMsgThrd: error
                                              }}
                                            />
                                            <BtnModal
                                              modalSize='lg'
                                              modalTitle={`Edit Default Prices for ${selProduct?.languages?.eng?.title}`}
                                              modalBtnLabel='Edit Default Prices'
                                              modalOpen={modalEditPrice}
                                              toggleModal={toggleEditPriceModal}
                                              handleSubmit={handleNewProductSubmit}
                                            >
                                              {modalPriceContent}
                                            </BtnModal>
                                            <BtnModal
                                              modalSize='xl'
                                              modalTitle={`Edit Regional Prices for ${selProduct?.languages?.eng?.title}`}
                                              modalBtnLabel='Edit Regional Prices'
                                              modalOpen={modalEditRegPrice}
                                              toggleModal={toggleEditRegPriceModal}
                                              handleSubmit={handleNewProductSubmit}
                                            >
                                              {modalRegPriceContent}
                                            </BtnModal>
                                            <SweetAlert
                                              show={showDeleteAlert}
                                              danger
                                              showCancel
                                              confirmBtnText='Yes, delete it!'
                                              confirmBtnBsStyle='danger'
                                              title='Are you sure?'
                                              onConfirm={handleDeleteProduct}
                                              onCancel={() => setShowDeleteAlert(false)}
                                              focusCancelBtn
                                            >
                                              <p className='fw-bold'>{selProduct?.languages?.eng?.title}</p> will be deleted from the system!
                                            </SweetAlert>
                                          </React.Fragment>
                                        )}
                                      </ToolkitProvider>
                                    )}
                                  </CardBody>
                                </Card>
                              </Col>
                            </Row>
                          </div>
                        </motion.li>
                      </AnimatePresence>
                    </ul>
                  </div>
                </Col>
              </Row>
            </>
          ) : (
            <Alert color='danger'> You do not have access to this page. </Alert>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
}
